<template>
  <Teleport to="body">
    <Transition name="collection-overlay" :duration="origin ? 360 : undefined">
      <div
        v-if="visible"
        class="CollectionOverlay"
        :class="{ 'from-sphere': !!origin }"
        :style="bloomStyle"
        @click.self="emit('close')"
      >
        <button
          type="button"
          class="collection-brand"
          :aria-label="localize('handshake.collection.title')"
          @click.stop="openStory"
        >
          <h2>
            <span class="collection-figure">{{ collectionTitle[0] }}</span>
            <HandshakeMetric class="header-master" :icon="GamIconName.CROWN" aria-hidden="true" />
            <span class="collection-figure">{{ collectionTitle[1] }}</span>
          </h2>
        </button>

        <div class="collection-wrapper" :class="{ 'is-mobile': isMobile }">
          <div ref="cardRef" class="collection-card">
            <!-- Gray band the pawn bodies stand on. The marks themselves hang above this edge. -->
            <div class="collection-heading"></div>

            <div ref="scrollRef" class="collection-scroll">
              <div
                class="collection-content"
                :class="{
                  'is-pairs': !boardOpen && activeTab === 'pairs',
                  'has-lens':
                    !boardOpen && (activeTab === 'positions' || activeTab === 'people' || activeTab === 'boards'),
                }"
              >
                <template v-if="boardConnected && boardOpen">
                  <div class="road is-toggle" :class="{ 'is-open': roadOpen }" @click="toggleRoad">
                    <div class="road-heading">
                      <h3 class="road-title">
                        {{ localize('handshake.collection.tabs.milestones') }}
                        <button
                          type="button"
                          class="road-icon"
                          :class="{ 'is-open': roadOpen }"
                          :aria-expanded="roadOpen"
                          :aria-label="
                            localize(roadOpen ? 'handshake.collection.road.hide' : 'handshake.collection.road.show')
                          "
                          @click.stop="toggleRoad"
                        >
                          <GamIcon :name="GamIconName.ROUTE" :size="GamIconSize.NORMAL" is-current />
                        </button>
                      </h3>
                      <span class="header-square is-road" :class="`is-${headerMark.tone}`">
                        <span v-if="headerMark.text" class="header-coord">{{ headerMark.text }}</span>
                      </span>
                    </div>
                    <div v-if="roadOpen" class="road-marks" :style="{ '--nodes': String(roadNodes.length) }">
                      <span v-if="isMaster" class="now-gap">
                        <span class="gap-text">{{ localize('handshake.collection.gambitsMaster') }}</span>
                      </span>
                      <div v-for="node in roadNodes" :key="node.key" class="road-node">
                        <span class="road-mark-row">
                          <span
                            v-if="node.nextMark"
                            class="road-connector"
                            :class="`is-${node.line}`"
                            :style="{
                              '--mark-r': node.gained || node.missing ? '0.75rem' : '4px',
                              '--next-r': node.nextChip ? '0.75rem' : '4px',
                              '--walk': `${node.walk}%`,
                            }"
                          />
                          <span v-if="node.gained" class="road-delta is-gained" :class="`is-${node.tone}`"
                            >+{{ node.gained }}</span
                          >
                          <span v-else-if="node.missing" class="road-delta is-missing" :class="`is-${node.tone}`"
                            >-{{ node.missing }}</span
                          >
                          <span v-else class="road-mark" :class="`is-${node.tone}`" />
                        </span>
                        <span class="road-labels">
                          <span
                            v-if="node.notation"
                            class="road-notation"
                            :class="node.key !== 'start' ? `is-${node.tone}` : undefined"
                            >{{ node.notation }}</span
                          >
                          <span v-if="node.value" class="road-figure">{{ node.value }}</span>
                        </span>
                      </div>
                      <span v-if="roadYou" class="road-you" :style="roadYouStyle">
                        <span class="road-you-pin">
                          <span class="road-you-count">{{ roadYou.count }}</span>
                          <span v-if="roadYou.dot" class="road-you-dot" />
                        </span>
                      </span>
                    </div>
                  </div>
                  <section class="stage-board is-overview">
                    <div class="board-plot">
                      <div class="file-axis">
                        <span v-for="mark in fileAxis" :key="mark.mark" class="axis-slot">
                          <i v-if="mark.dot" class="axis-dot" :style="{ '--fill': mark.fill }" />
                          <span class="axis-count" :class="{ 'is-dot': mark.dot }" :style="{ '--fill': mark.fill }">{{
                            mark.mark
                          }}</span>
                        </span>
                      </div>
                      <div class="rank-axis">
                        <span v-for="mark in rankAxis" :key="mark.mark" class="axis-slot">
                          <i v-if="mark.dot" class="axis-dot" :style="{ '--fill': mark.fill }" />
                          <span
                            class="axis-count is-rank"
                            :class="{ 'is-dot': mark.dot }"
                            :style="{ '--fill': mark.fill }"
                          >
                            <span class="rank-num">{{ mark.mark }}</span>
                          </span>
                        </span>
                      </div>
                      <span class="axis-name is-people">{{ localize('handshake.collection.tabs.people') }}</span>
                      <span class="axis-name is-positions">{{ localize('handshake.collection.tabs.positions') }}</span>
                      <div class="board-legend">
                        <span class="board-legend-item">
                          <i class="board-swatch is-pairs" />
                          {{ localize('handshake.collection.board.legend.pairs') }}
                        </span>
                        <span class="board-legend-item">
                          <i class="board-swatch is-axes" />
                          {{ localize('handshake.collection.board.legend.axes') }}
                        </span>
                      </div>
                      <div class="board-frame">
                        <div class="board-grid">
                          <div
                            v-for="square in boardSquares"
                            :key="square.key"
                            class="board-square"
                            :class="{
                              'is-first-file': square.isFirstFile,
                              'is-top-rank': square.isTopRank,
                              'is-axis': square.axis,
                            }"
                            :style="{ '--fill': square.fill }"
                          />
                        </div>
                      </div>
                    </div>
                  </section>
                </template>

                <template v-else-if="activeTab === 'pairs'">
                  <section class="pair-stage">
                    <div class="pair-summary">
                      <div class="collection-featured" :class="{ 'is-quiet': !pairLinesShown }">
                        <div class="featured-mark">
                          <div
                            v-for="layer in pairLayers"
                            :key="layer.id"
                            :ref="(el) => bindPairLayer(layer.id, el)"
                            class="featured-layer"
                            :class="{ 'is-entering': layer.id === enteringPairId }"
                          >
                            <HandshakePositionMark
                              :position-index="layer.pair.index"
                              :stand-in-face="layer.pair.standIn !== null"
                              :face="layer.pair.image"
                              :photos="pairLinesShown ? highlightedPhotos : []"
                              :photo-size="160"
                              align="center"
                              show-face
                              @open-photo="openHighlightedPhoto()"
                            />
                          </div>
                          <HandshakePositionMark
                            v-if="!pairLayers.length"
                            class="is-empty"
                            :position-index="0"
                            align="center"
                            hide-pieces
                            show-face
                          />
                        </div>
                        <div class="featured-lines" :class="{ 'is-shown': pairLinesShown }">
                          <p class="featured-caption">
                            <template v-if="highlightedPair">
                              <span :class="highlightedPair.standIn !== null ? 'is-reported' : 'is-unique'"
                                >#{{ highlightedPair.index }}</span
                              >
                              ・
                              <span
                                v-if="highlightedPair.username"
                                :class="highlightedPair.standIn !== null ? 'is-reported' : 'is-unique'"
                                >@{{ highlightedPair.username }}</span
                              >
                              <span
                                v-else-if="highlightedPair.standIn"
                                :class="highlightedPair.standIn !== null ? 'is-reported' : 'is-unique'"
                                >{{ highlightedPair.standIn.name }}</span
                              >
                            </template>
                            <template v-else># ・ {{ localize('handshake.collection.empty.pairs') }}</template>
                          </p>
                          <p v-if="highlightedPair" class="collection-latest">
                            {{ highlightedPairLabel
                            }}<template v-if="highlightedPairMonth"> ・ {{ highlightedPairMonth }}</template>
                          </p>
                        </div>
                      </div>
                      <button type="button" class="pair-caption is-count" @click="playPairs">
                        <span class="pair-count">{{ uniquePairs }}</span>
                        <span class="pair-count-rest">{{ localize('handshake.collection.progress') }}</span>
                      </button>
                    </div>
                    <div class="pair-minis" :class="{ 'has-review': standInCandidates.length }">
                      <button
                        v-for="item in pairMinis"
                        :key="item.key"
                        type="button"
                        class="pair-bond is-mini"
                        :class="{
                          'is-empty': !item.pair,
                          'is-reported': item.pair && item.pair.standIn !== null,
                          'is-chosen': item.pair && highlightedPair?.index === item.pair.index,
                        }"
                        :disabled="!item.pair"
                        @click="item.pair && showPair(item.pair.index)"
                      >
                        <div class="pair-square">{{ item.pair ? `#${item.pair.index}` : '#' }}</div>
                        <div class="pair-photo" :class="{ 'is-stand-in': item.pair && item.pair.standIn !== null }">
                          <img v-if="item.pair?.image" :src="getImageSrc(item.pair.image)" alt="" />
                          <GamIcon v-else :name="GamIconName.PAWN_EMPTY" size="11px" is-current />
                        </div>
                      </button>
                    </div>
                  </section>
                </template>

                <template v-else-if="showingRoad">
                  <div class="road is-toggle" :class="{ 'is-open': roadOpen }" @click="toggleRoad">
                    <div class="road-heading">
                      <h3 class="road-title">
                        {{ localize(overviewTitleKey) }}
                        <button
                          type="button"
                          class="road-icon"
                          :class="{ 'is-open': roadOpen }"
                          :aria-expanded="roadOpen"
                          :aria-label="
                            localize(roadOpen ? 'handshake.collection.road.hide' : 'handshake.collection.road.show')
                          "
                          @click.stop="toggleRoad"
                        >
                          <GamIcon :name="GamIconName.ROUTE" :size="GamIconSize.NORMAL" is-current />
                        </button>
                      </h3>
                      <span class="road-count">{{ roadCount }}</span>
                    </div>
                    <div v-if="roadOpen" class="road-marks" :style="{ '--nodes': String(roadNodes.length) }">
                      <span v-if="isMaster" class="now-gap">
                        <span class="gap-text">{{ localize('handshake.collection.gambitsMaster') }}</span>
                      </span>
                      <span v-else-if="overviewChip" class="now-gap">
                        <span class="gap-text">
                          {{ localize('handshake.collection.nextMilestone.with') }}
                          <span class="gap-value">{{ overviewChip.left }}</span>
                          {{ localize(uniqueChipLabel(overviewChip)) }}
                        </span>
                      </span>
                      <div v-for="node in roadNodes" :key="node.key" class="road-node">
                        <span class="road-mark-row">
                          <span
                            v-if="node.nextMark"
                            class="road-connector"
                            :class="`is-${node.line}`"
                            :style="{
                              '--mark-r': node.gained || node.missing ? '0.75rem' : '4px',
                              '--next-r': node.nextChip ? '0.75rem' : '4px',
                              '--walk': `${node.walk}%`,
                            }"
                          />
                          <span v-if="node.gained" class="road-delta is-gained" :class="`is-${node.tone}`"
                            >+{{ node.gained }}</span
                          >
                          <span v-else-if="node.missing" class="road-delta is-missing" :class="`is-${node.tone}`"
                            >-{{ node.missing }}</span
                          >
                          <span v-else class="road-mark" :class="`is-${node.tone}`" />
                        </span>
                        <span class="road-labels">
                          <span
                            v-if="node.notation"
                            class="road-notation"
                            :class="node.key !== 'start' ? `is-${node.tone}` : undefined"
                            >{{ node.notation }}</span
                          >
                          <span v-if="node.value" class="road-figure">{{ node.value }}</span>
                        </span>
                      </div>
                      <span v-if="roadYou" class="road-you" :style="roadYouStyle">
                        <span class="road-you-pin">
                          <span class="road-you-count">{{ roadYou.count }}</span>
                          <span v-if="roadYou.dot" class="road-you-dot" />
                        </span>
                      </span>
                    </div>
                  </div>
                  <section v-if="activeTab === 'positions'" class="ring">
                    <div class="donut-anchor">
                      <div class="donut is-hero">
                        <svg viewBox="0 0 120 120" aria-hidden="true">
                          <g transform="rotate(-90 60 60)">
                            <circle class="donut-track" cx="60" cy="60" :r="DONUT_RADIUS" />
                            <circle
                              v-for="arc in positionArcs"
                              :key="arc.tone"
                              class="donut-arc"
                              :class="`is-${arc.tone}`"
                              cx="60"
                              cy="60"
                              :r="DONUT_RADIUS"
                              :stroke-dasharray="arc.dash"
                              :stroke-dashoffset="arc.offset"
                            />
                          </g>
                        </svg>
                        <p class="collection-progress">
                          {{ localizeWithValues('handshake.collection.percent', [percentOf960(playedCount)]) }}
                        </p>
                      </div>
                    </div>

                    <div class="legend-block">
                      <div class="legend-title">{{ localize('handshake.collection.legend.hasPair') }}</div>
                      <div class="legend is-row">
                        <span class="legend-item">
                          <span class="legend-line">
                            <i class="swatch is-unique" />
                            {{ localize('handshake.collection.legend.unique') }}
                            <span class="legend-count">{{ positionCounts.unique }}</span>
                          </span>
                          <span class="legend-pct is-unique">{{
                            localizeWithValues('handshake.collection.percent', [percentOf960(positionCounts.unique)])
                          }}</span>
                        </span>
                        <span v-if="positionCounts.reported" class="legend-item">
                          <span class="legend-line">
                            <i class="swatch is-reported" />
                            {{ localize('handshake.collection.legend.reported') }}
                            <span class="legend-count">{{ positionCounts.reported }}</span>
                          </span>
                          <span class="legend-pct is-reported">{{
                            localizeWithValues('handshake.collection.percent', [percentOf960(positionCounts.reported)])
                          }}</span>
                        </span>
                        <span class="legend-item">
                          <span class="legend-line">
                            <i class="swatch is-repeat" />
                            {{ localize('handshake.collection.legend.repeat') }}
                            <span class="legend-count">{{ positionCounts.repeat }}</span>
                          </span>
                          <span class="legend-pct is-repeat">{{
                            localizeWithValues('handshake.collection.percent', [percentOf960(positionCounts.repeat)])
                          }}</span>
                        </span>
                        <span class="legend-item">
                          <span class="legend-line">
                            <i class="swatch is-unknown" />
                            {{ localize('handshake.collection.legend.unknown') }}
                            <span class="legend-count">{{ positionCounts.unknown }}</span>
                          </span>
                          <span class="legend-pct is-unknown">{{
                            localizeWithValues('handshake.collection.percent', [percentOf960(positionCounts.unknown)])
                          }}</span>
                        </span>
                      </div>
                      <p v-if="countedAnonymousParts.length" class="not-counted">
                        {{ localize('handshake.collection.countedWith') }}
                        <template v-for="part in countedAnonymousParts" :key="part.key">
                          · {{ part.label }} <span class="legend-count">{{ part.count }}</span>
                        </template>
                      </p>
                    </div>
                  </section>
                  <section v-else class="ring">
                    <div class="donut-anchor">
                      <div class="donut is-hero">
                        <svg viewBox="0 0 120 120" aria-hidden="true">
                          <g transform="rotate(-90 60 60)">
                            <circle class="donut-track" cx="60" cy="60" :r="DONUT_RADIUS" />
                            <circle
                              v-for="arc in peopleArcs"
                              :key="arc.tone"
                              class="donut-arc"
                              :class="`is-${arc.tone}`"
                              cx="60"
                              cy="60"
                              :r="DONUT_RADIUS"
                              :stroke-dasharray="arc.dash"
                              :stroke-dashoffset="arc.offset"
                            />
                          </g>
                        </svg>
                        <p class="collection-progress">
                          {{ localizeWithValues('handshake.collection.percent', [percentOf960(peopleRingTotal)]) }}
                        </p>
                      </div>
                    </div>

                    <div class="legend-block">
                      <div class="legend-title">{{ localize('handshake.collection.legend.hasPair') }}</div>
                      <div class="legend is-row">
                        <span class="legend-item">
                          <span class="legend-line">
                            <i class="swatch is-unique" />
                            {{ localize('handshake.collection.legend.unique') }}
                            <span class="legend-count">{{ peopleRingCounts.unique }}</span>
                          </span>
                          <span class="legend-pct is-unique">{{
                            localizeWithValues('handshake.collection.percent', [percentOf960(peopleRingCounts.unique)])
                          }}</span>
                        </span>
                        <span v-if="peopleRingCounts.reported" class="legend-item">
                          <span class="legend-line">
                            <i class="swatch is-reported" />
                            {{ localize('handshake.collection.legend.reported') }}
                            <span class="legend-count">{{ peopleRingCounts.reported }}</span>
                          </span>
                          <span class="legend-pct is-reported">{{
                            localizeWithValues('handshake.collection.percent', [
                              percentOf960(peopleRingCounts.reported),
                            ])
                          }}</span>
                        </span>
                        <span class="legend-item">
                          <span class="legend-line">
                            <i class="swatch is-repeat" />
                            {{ localize('handshake.collection.legend.repeat') }}
                            <span class="legend-count">{{ peopleRingCounts.repeat }}</span>
                          </span>
                          <span class="legend-pct is-repeat">{{
                            localizeWithValues('handshake.collection.percent', [percentOf960(peopleRingCounts.repeat)])
                          }}</span>
                        </span>
                        <span class="legend-item">
                          <span class="legend-line">
                            <i class="swatch is-none" />
                            {{ localize('handshake.collection.legend.none') }}
                            <span class="legend-count">{{ peopleRingCounts.none }}</span>
                          </span>
                          <span class="legend-pct is-none">{{
                            localizeWithValues('handshake.collection.percent', [percentOf960(peopleRingCounts.none)])
                          }}</span>
                        </span>
                      </div>
                      <p v-if="notCountedParts.length" class="not-counted">
                        {{ localize('handshake.collection.notCounted') }}
                        <template v-for="part in notCountedParts" :key="part.key">
                          · {{ part.label }} <span class="legend-count">{{ part.count }}</span>
                        </template>
                      </p>
                    </div>
                  </section>
                </template>

                <template v-else-if="activeTab === 'people'">
                  <template v-if="handshakeRows.length">
                    <div class="people-head">
                      <span>{{ localize('handshake.collection.tabs.positions') }}</span>
                    </div>
                    <template v-for="item in peopleList" :key="item.key">
                      <div v-if="!item.row" class="people-split" />
                      <div
                        v-else
                        class="player-row"
                        :class="{
                          'is-link': !!item.row.username,
                          'is-unknown': item.row.kind !== 'account' && item.row.kind !== 'reported',
                        }"
                        @click="item.row.username && openPlayer(item.row.username)"
                      >
                        <div class="player-avatar" :class="{ 'is-stand-in': item.row.kind === 'reported' }">
                          <img v-if="item.row.image" :src="getImageSrc(item.row.image)" class="picture" />
                          <GamIcon v-else :name="GamIconName.PAWN_EMPTY" size="11px" is-current />
                        </div>
                        <span class="player-line">
                          <span class="player-username">{{ item.row.name }}</span>
                          <span class="player-positions">{{ item.row.count }}</span>
                        </span>
                      </div>
                    </template>
                  </template>
                  <div v-else class="empty-state">
                    <GamIcon :name="GamIconName.HANDSHAKE_EMPTY" size="24px" is-disabled />
                    <span>{{ localize('players.modal.empty.handshakes') }}</span>
                  </div>
                </template>

                <template v-else-if="isOwner && activeTab === 'boards'">
                  <CollectionBoards :view="boardsView" @close="emit('close')" />
                </template>

                <template v-else>
                  <template v-if="positionView === 'list'">
                    <div v-if="positions.length" class="position-list">
                      <HandshakePositionMark
                        v-for="position in positions"
                        :key="position.index"
                        :position-index="position.index"
                        :partners="position.usernames"
                        :unique-partner="position.uniqueUsername"
                        :stand-ins="position.standIns"
                        :unique-stand-in="position.uniqueStandIn"
                        :stand-in-face="position.tone === 'reported'"
                        :photos="position.photos"
                        :number-color="position.tone"
                        :show-face="position.tone === 'unique' || position.tone === 'reported'"
                        :face="pairImage(position.uniqueUsername)"
                        align="start"
                        show-number
                        show-partner-dot
                        @open-photo="openPositionPhoto(position, $event)"
                      />
                    </div>
                    <div v-else class="empty-state">
                      <span>{{ localize('handshake.collection.empty.positions') }}</span>
                    </div>
                  </template>

                  <template v-else-if="positionView === 'table'">
                    <div v-if="selected" class="position-detail">
                      <HandshakePositionMark
                        :position-index="selected.index"
                        :partners="selected.usernames"
                        :unique-partner="selected.uniqueUsername"
                        :stand-ins="selected.standIns"
                        :unique-stand-in="selected.uniqueStandIn"
                        :stand-in-face="selected.tone === 'reported'"
                        :photos="selected.photos"
                        :number-color="selected.tone"
                        :show-face="selected.tone === 'unique' || selected.tone === 'reported'"
                        :face="pairImage(selected.uniqueUsername)"
                        align="start"
                        show-number
                        show-partner-dot
                        @open-photo="openPositionPhoto(selected, $event)"
                      >
                        <template #side>
                          <button type="button" class="position-play" @click.stop="playPosition">
                            {{ localize('handshake.collection.play') }}
                          </button>
                        </template>
                      </HandshakePositionMark>
                      <p v-if="selectedToneLabel" class="position-tone-line" :class="`is-${selected.tone}`">
                        {{ selectedToneLabel }}
                      </p>
                    </div>

                    <div class="position-table">
                      <button
                        v-for="index in POSITION_INDEXES"
                        :key="index"
                        type="button"
                        :class="[
                          'position-cell',
                          `is-${collection.tones[index]}`,
                          { 'is-selected': selectedIndex === index },
                        ]"
                        @click="selectedIndex = selectedIndex === index ? null : index"
                      >
                        {{ index }}
                      </button>
                    </div>
                  </template>
                </template>
              </div>
            </div>

            <div class="collection-dock">
              <button
                v-if="!boardOpen && activeTab === 'pairs' && standInCandidates.length"
                type="button"
                class="collection-review"
                @click="unknownReviewOpen = true"
              >
                {{ localize('handshake.unknownReview.cta') }} · {{ standInCandidates.length }}
              </button>
              <NavPill v-else-if="!boardOpen && activeTab === 'positions'" class="collection-lens">
                <NavPillButton
                  :class="{ 'is-chosen': positionView === 'table' }"
                  :aria-label="localize('handshake.collection.view.table')"
                  @select="showPositionView('table')"
                >
                  <GamIcon :name="GamIconName.PUZZLE_GRID" :size="GamIconSize.NORMAL" is-current />
                </NavPillButton>
                <NavPillButton
                  :class="{ 'is-chosen': positionView === 'list' }"
                  :aria-label="localize('handshake.collection.view.list')"
                  @select="showPositionView('list')"
                >
                  <GamIcon :name="GamIconName.BULLET_LIST" :size="GamIconSize.NORMAL" is-current />
                </NavPillButton>
                <NavPillButton
                  :class="{ 'is-chosen': positionView === 'road' }"
                  :aria-label="localize('handshake.collection.view.road')"
                  @select="showPositionView('road')"
                >
                  <GamIcon :name="GamIconName.ROUTE" :size="GamIconSize.NORMAL" is-current />
                </NavPillButton>
              </NavPill>
              <NavPill v-else-if="!boardOpen && activeTab === 'people'" class="collection-lens">
                <NavPillButton
                  :class="{ 'is-chosen': peopleView === 'list' }"
                  :aria-label="localize('handshake.collection.view.list')"
                  @select="showPeopleView('list')"
                >
                  <GamIcon :name="GamIconName.BULLET_LIST" :size="GamIconSize.NORMAL" is-current />
                </NavPillButton>
                <NavPillButton
                  :class="{ 'is-chosen': peopleView === 'road' }"
                  :aria-label="localize('handshake.collection.view.road')"
                  @select="showPeopleView('road')"
                >
                  <GamIcon :name="GamIconName.ROUTE" :size="GamIconSize.NORMAL" is-current />
                </NavPillButton>
              </NavPill>
              <NavPill v-else-if="!boardOpen && isOwner && activeTab === 'boards'" class="collection-lens">
                <NavPillButton
                  :class="{ 'is-chosen': boardsView === 'active' }"
                  :aria-label="localize('handshake.collection.view.active')"
                  @select="showBoardsView('active')"
                >
                  <GamIcon :name="GamIconName.LOCATION" :size="GamIconSize.NORMAL" is-current />
                </NavPillButton>
                <NavPillButton
                  :class="{ 'is-chosen': boardsView === 'completed' }"
                  :aria-label="localize('handshake.collection.view.completed')"
                  @select="showBoardsView('completed')"
                >
                  <GamIcon :name="GamIconName.CHECK" :size="GamIconSize.NORMAL" is-current />
                </NavPillButton>
              </NavPill>

              <div class="collection-dock-row">
                <nav class="collection-scopes" :class="{ 'is-wide': scopeTabs.length > 3 }">
                  <button
                    v-for="tab in scopeTabs"
                    :key="tab"
                    type="button"
                    class="collection-scope"
                    :class="{ 'is-chosen': !boardOpen && activeTab === tab }"
                    :aria-current="!boardOpen && activeTab === tab ? 'page' : undefined"
                    @click="showTab(tab)"
                  >
                    <span class="collection-scope-icon">
                      <span v-if="tab === 'positions'" class="collection-scope-hash">#</span>
                      <span v-else-if="tab === 'boards'" class="collection-scope-board" />
                      <GamIcon v-else :name="scopeIcon(tab)" :size="GamIconSize.LARGE" is-current />
                    </span>
                    <span class="collection-scope-label">{{ scopeLabel(tab) }}</span>
                  </button>
                </nav>
                <button type="button" class="collection-close" @click="emit('close')" @touchend.prevent="emit('close')">
                  <GamIcon :name="GamIconName.X_CLOSE" :size="GamIconSize.X_LARGE" is-current />
                </button>
              </div>
            </div>
          </div>

          <ol class="pair-milestones" :aria-label="localize('handshake.collection.tabs.pairs')">
            <li
              v-for="mark in pairMilestones"
              :key="mark.value"
              class="pair-milestone"
              :class="{ 'is-reached': mark.reached }"
            >
              <span class="pair-milestone-mark" aria-hidden="true">
                <GamIcon :name="GamIconName.PAWN" size="36px" is-current />
                <span v-if="mark.foot" class="pair-milestone-fill" :style="{ '--pawn-rise': String(mark.body) }">
                  <GamIcon :name="GamIconName.PAWN" size="36px" is-current />
                </span>
              </span>
              <span class="pair-milestone-need">{{ mark.value }}</span>
            </li>
          </ol>
        </div>
      </div>
    </Transition>

    <GamExpandImage
      v-if="selectedImage"
      v-bind="selectedImage"
      ref="imageExpand"
      show-author
      is-gallery
      is-url-bound
      :can-add="canAddGalleryPhoto"
      :add-shortcut="galleryAddShortcut"
      @close="closePositionPhoto"
      @add="addGalleryPhoto"
      @delete="deleteGalleryPhoto"
      @position="openPhotoPosition"
    />

    <HandshakePhotoPositionModal
      v-if="photoPosition"
      :handshake-id="photoPosition.handshakeId"
      :positions="photoPosition.positions"
      :current="photoPosition.positionIndex"
      :taken="photoPosition.taken"
      :meeting-full="photoPosition.meetingFull"
      :image-hash="photoPosition.hash"
      @close="photoPosition = null"
      @change="rememberMovedPhoto"
    />

    <MastersBook v-if="storyOpen" @close="storyOpen = false" />

    <HandshakeUnknownReview
      v-if="unknownReviewOpen"
      :candidates="standInCandidates"
      @decided="applyStandInVerdict"
      @open-photo="openReviewPhoto"
      @close="unknownReviewOpen = false"
    />
  </Teleport>
</template>

<script lang="ts" setup>
import { RouterNameType } from '@/core/data/config/uiConfig.interface';
import type { HandshakeDto } from '@/core/data/handshake/handshake.interface';
import { BlobSource } from '@/core/data/report/report.type';
import { gambits, localize, localizeWithValues } from '@/core/gambits';
import { formatMonthLabel } from '@/core/helpers/date.helper';
import { isMobile } from '@/core/helpers/ui.helper';
import { useAuthStore } from '@/stores/auth.store';
import GamExpandImage from '@/views/components/GamExpandImage.vue';
import GamIcon from '@/views/components/GamIcon.vue';
import MastersBook from '@/views/components/MastersBook.vue';
import NavPill from '@/views/components/NavPill.vue';
import NavPillButton from '@/views/components/NavPillButton.vue';
import { GamIconName, GamIconSize } from '@/views/composables/constants/components/gamIcon.constants';
import { useDrawerGesture } from '@/views/composables/hooks/useDrawerGesture';
import { useHandshakePhotoUpload } from '@/views/composables/hooks/useHandshakePhotoUpload';
import type { GamExpandImageType } from '@/views/composables/models/components/GamImage';
import CollectionBoards from '@/views/main/handshakes/CollectionBoards.vue';
import HandshakeMetric from '@/views/main/handshakes/HandshakeMetric.vue';
import HandshakePhotoPositionModal from '@/views/main/handshakes/HandshakePhotoPositionModal.vue';
import HandshakePositionMark from '@/views/main/handshakes/HandshakePositionMark.vue';
import HandshakeUnknownReview from '@/views/main/handshakes/HandshakeUnknownReview.vue';
import { isOpenHandshake } from '@/views/main/handshakes/utils/handshake-game';
import { photosInSlot, photosToUpload, positionsAtPhotoLimit } from '@/views/main/handshakes/utils/handshake-photos';
import {
  collectPositions,
  isReportedStandIn,
  standInLabel,
  type CollectedPhoto,
  type StandInMark,
} from '@/views/main/handshakes/utils/position-collection';
import { useFileDialog, useNow } from '@vueuse/core';
import { storeToRefs } from 'pinia';
import { computed, nextTick, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';

type CollectionTab = 'pairs' | 'positions' | 'people' | 'boards';
/** Older links still ask for the overview carousel or its board slide. */
type CollectionEntry = CollectionTab | 'overview' | 'milestones' | 'handshakes';

function scopeLabel(tab: CollectionTab) {
  return localize(tab === 'pairs' ? 'handshake.collection.tabs.overview' : `handshake.collection.tabs.${tab}`);
}

function scopeIcon(tab: CollectionTab): GamIconName {
  return tab === 'people' ? GamIconName.PROFILE : GamIconName.HANDSHAKE;
}

const props = defineProps<{
  visible: boolean;
  /** Whose handshakes these are, so the other name in each row is the person met. */
  userId: string;
  handshakes: HandshakeDto[];
  /** The profile photo beside the title. */
  profileImage?: string | null;
  initialTab?: CollectionEntry;
  /** Where the sheet grows from. Absent, and it rises as a drawer. */
  origin?: { x: number; y: number; size: number } | null;
}>();

const emit = defineEmits<{ close: []; update: [] }>();

/** The sheet starts as a circle on the sphere, then settles into the drawer. */
const bloomStyle = computed(() => {
  const origin = props.origin;
  if (!origin) return undefined;

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const mobile = !!isMobile.value;
  const root = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
  const cardW = mobile ? vw : Math.min(vw - 2 * root, 40 * root);
  const pawnOverhang = 36 * 0.3115 + 2;
  const brandClearance = 0.85 * root + 30 + 0.85 * root + pawnOverhang;
  const cardH = mobile ? vh * 0.85 : Math.min(700, vh - brandClearance - 2 * root);
  const drawerTop = mobile ? vh - cardH : brandClearance + (vh - brandClearance - cardH) / 2;
  const dx = origin.x - vw / 2;
  const dy = origin.y - (drawerTop + cardH / 2);

  return {
    '--bloom-x': `${dx}px`,
    '--bloom-y': `${dy}px`,
    '--bloom-sx': cardW > 0 ? String(origin.size / cardW) : '0.2',
    '--bloom-sy': cardH > 0 ? String(origin.size / cardH) : '0.2',
  };
});

const readTab = (tab?: CollectionEntry): CollectionTab => {
  if (tab === 'handshakes') return 'people';
  if (tab === 'positions' || tab === 'people' || tab === 'pairs' || tab === 'boards') return tab;

  return 'pairs';
};

const readBoard = (tab?: CollectionEntry) => tab === 'milestones';

const router = useRouter();
const route = useRoute();
const storyOpen = ref(false);
/** The milestone board stays in this file. Nothing opens it. */
const boardConnected = ref(false);
const { user } = storeToRefs(useAuthStore());
const now = useNow({ interval: 60_000 });
const imageExpand = ref<InstanceType<typeof GamExpandImage>>();
const selectedImage = ref<GamExpandImageType | null>(null);
const galleryPosition = ref<number | null>(null);
const toast = useToast();
const { upload: uploadPhoto, settle: settlePhotos } = useHandshakePhotoUpload();
const { open: openGalleryFile, onChange: onGalleryFile } = useFileDialog({
  accept: '.jpg, .jpeg, .png',
  multiple: true,
});
const galleryUpload = ref<{ handshakeId: string; positionIndex: number } | null>(null);

onGalleryFile(async (files) => {
  const target = galleryUpload.value;
  galleryUpload.value = null;
  if (!files?.length || !target || !user.value) return;

  const handshake = props.handshakes.find((item) => item.id === target.handshakeId);
  const already = photosInSlot(handshake ?? {}, user.value.id, target.positionIndex);
  const batch = photosToUpload(files, already);
  if (!batch.length || batch.length < files.length) toast.error(localize('handshake.photo.taken'));
  if (!batch.length) return;

  await uploadPhoto(target.handshakeId, batch, target.positionIndex);
});
const photoPosition = ref<{
  handshakeId: string;
  hash: string;
  ownerId: string | null;
  positionIndex: number | null;
  positions: number[];
  taken: number[];
  meetingFull: boolean;
} | null>(null);
const activeTab = ref<CollectionTab>(readTab(props.initialTab));
const boardOpen = ref(false);
const positionView = ref<'list' | 'table' | 'road'>('table');
const peopleView = ref<'list' | 'road'>('list');
const boardsView = ref<'active' | 'completed'>('active');
const selectedIndex = ref<number | null>(null);
const selectedPairIndex = ref<number | null>(null);
/** Which pair the tour is showing. A tapped mini replaces the tour. */
const cycleIndex = ref(0);
let pairCycleTimer: number | null = null;
let pairFade: Animation | null = null;
/** True while the next pair is dissolving in, so the tour owns the featured layers. */
let pairCrossfading = false;

function stopPairFade() {
  pairFade?.cancel();
  pairFade = null;
  pairCrossfading = false;
}

function clearPairTour() {
  if (pairCycleTimer != null) {
    window.clearInterval(pairCycleTimer);
    pairCycleTimer = null;
  }
  stopPairFade();
}
const ROAD_CLOSED_KEY = 'gambits.collection.road-closed';

/** Open unless it was closed earlier in this visit. */
function readRoadOpen(): boolean {
  try {
    return sessionStorage.getItem(ROAD_CLOSED_KEY) !== '1';
  } catch {
    return true;
  }
}

function rememberRoad(open: boolean) {
  try {
    if (open) sessionStorage.removeItem(ROAD_CLOSED_KEY);
    else sessionStorage.setItem(ROAD_CLOSED_KEY, '1');
  } catch {
    // Private mode can reject storage. The choice still holds until the overlay closes.
  }
}

const roadOpen = ref(readRoadOpen());
const cardRef = ref<HTMLElement | null>(null);
const scrollRef = ref<HTMLElement | null>(null);

/** One scroller serves every tab, so a long list would otherwise reopen the next tab mid-way down. */
function scrollCollectionToTop() {
  nextTick(() => {
    if (scrollRef.value) scrollRef.value.scrollTop = 0;
  });
}

/** The profile query uses overview for the pairs tab. */
function collectionPlayersQuery(tab: CollectionTab) {
  if (tab === 'people') return 'people';
  if (tab === 'positions') return 'positions';
  if (tab === 'boards') return 'boards';
  return 'overview';
}

/** Leave the current history entry on this tab, so closing a profile returns here. */
function rememberCollectionTab(tab: CollectionTab) {
  const players = collectionPlayersQuery(tab);
  if (route.query.players === players) return Promise.resolve();
  return router.replace({ ...route, query: { ...route.query, players } });
}

function showTab(tab: CollectionTab) {
  boardOpen.value = false;
  activeTab.value = tab;
  scrollCollectionToTop();
  void rememberCollectionTab(tab);
}

// The header no longer opens the board. This stays until the board itself is removed.
// eslint-disable-next-line @typescript-eslint/no-unused-vars
function showBoard() {
  if (!boardConnected.value) return;
  boardOpen.value = !boardOpen.value;
  scrollCollectionToTop();
}

function showPositionView(view: 'list' | 'table' | 'road') {
  positionView.value = view;
  scrollCollectionToTop();
}

function showPeopleView(view: 'list' | 'road') {
  peopleView.value = view;
  scrollCollectionToTop();
}

function showBoardsView(view: 'active' | 'completed') {
  boardsView.value = view;
  scrollCollectionToTop();
}
const POSITION_INDEXES = Array.from({ length: 960 }, (_, index) => index);
const BOARD_FILES = ['a', 'b', 'c', 'd', 'e', 'f', 'g', 'h'] as const;
const BOARD_STAGES = [1, 29, 96, 240, 480, 720, 900, 960] as const;

const stageIndex = (count: number) => {
  let index = -1;
  for (let i = 0; i < BOARD_STAGES.length; i++) {
    if (count >= BOARD_STAGES[i]) index = i;
  }
  return index;
};
const DONUT_RADIUS = 46;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;
/* Each arc runs a hair under the next, so no seam shows between colours. */
const DONUT_OVERLAP = 0.6;
/** Shortest arc that still reads, when one person would otherwise be thinner than a pixel. */
const MIN_ARC = 2;
const POSITION_TONES = ['unique', 'reported', 'repeat', 'unknown'] as const;
const PEOPLE_TONES = ['unique', 'reported', 'repeat', 'none'] as const;

useDrawerGesture(cardRef, () => emit('close'));

const openStory = () => {
  storyOpen.value = true;
};

const isOwner = computed(() => !!user.value && user.value.id === props.userId);

/** Boards belong to the signed-in player, so another profile keeps the three handshake scopes. */
const scopeTabs = computed<CollectionTab[]>(() => {
  const tabs: CollectionTab[] = ['pairs', 'positions', 'people'];
  if (isOwner.value) tabs.push('boards');
  return tabs;
});

watch(
  () => props.visible,
  (open) => {
    if (!open) {
      storyOpen.value = false;
      return;
    }
    const next = readTab(props.initialTab);
    activeTab.value = next === 'boards' && !isOwner.value ? 'pairs' : next;
    boardOpen.value = boardConnected.value && readBoard(props.initialTab);
    positionView.value = 'table';
    peopleView.value = 'list';
    boardsView.value = 'active';
    roadOpen.value = readRoadOpen();
    selectedIndex.value = null;
    selectedPairIndex.value = null;
    cycleIndex.value = 0;
    clearPairTour();
  },
);

/**
 * One row per account, with every position checked across their handshakes,
 * counted once. A passerby or ex-member marked unique is a person too, one row
 * per handshake, and that row counts positions. Every other pending passerby
 * shares one row, and every other handshake with no account shares another.
 * Those two rows count handshakes, so marking one unique raises reported by
 * one and lowers the row they left by one.
 */
const handshakeRows = computed(() => {
  const rows = new Map<
    string,
    {
      id: string;
      username?: string;
      name: string;
      image?: string | null;
      kind: 'account' | 'reported' | 'passerby' | 'unknown';
      positions: Set<number>;
      handshakes: number;
    }
  >();

  for (const handshake of props.handshakes) {
    if (!handshake.isConfirmed) continue;
    if (
      !(
        handshake.initiatorId ||
        handshake.responderId ||
        handshake.initiatorImageBlobHash ||
        handshake.responderImageBlobHash
      )
    ) {
      continue;
    }

    const partner = handshake.initiatorId === props.userId ? handshake.responder : handshake.initiator;
    const kind = partner?.id
      ? 'account'
      : isReportedStandIn(handshake, props.userId)
        ? 'reported'
        : handshake.isPasserbyPending
          ? 'passerby'
          : 'unknown';
    const key = partner?.id ?? (kind === 'reported' ? `reported:${handshake.id}` : kind);
    const row = rows.get(key) ?? {
      id: key,
      username: partner?.username,
      name: partner?.username ? `@${partner.username}` : kind === 'reported' ? standInLabel(handshake) : '',
      image: kind === 'account' ? partner?.profileImageBlobHash : null,
      kind,
      positions: new Set<number>(),
      handshakes: 0,
    };

    for (const position of handshake.positions ?? []) row.positions.add(position);
    row.handshakes += 1;
    rows.set(key, row);
  }

  const kindRank = { passerby: 0, unknown: 1, reported: 2, account: 3 };

  return [...rows.values()]
    .map(({ positions, ...row }) => ({
      ...row,
      name:
        row.kind === 'passerby'
          ? localize(row.handshakes === 1 ? 'handshake.player.passerby' : 'handshake.player.passersby')
          : row.kind === 'unknown' || !row.name
            ? localize('handshake.player.unknown')
            : row.name,
      count: row.kind === 'passerby' || row.kind === 'unknown' ? row.handshakes : positions.size,
    }))
    .sort((a, b) => kindRank[a.kind] - kindRank[b.kind]);
});

/**
 * Passersby and unknown stay out of the people count. The same handshake
 * total is named under both rings, so a self-reported pair lowers one of them by one.
 */
const notCountedParts = computed(() => {
  const handshakesOf = (kind: 'passerby' | 'unknown') =>
    handshakeRows.value.find((row) => row.kind === kind)?.handshakes ?? 0;
  const passersby = handshakesOf('passerby');
  const unknown = handshakesOf('unknown');

  return [
    {
      key: 'passerby',
      label: localize(passersby === 1 ? 'handshake.player.passerby' : 'handshake.player.passersby'),
      count: passersby,
    },
    { key: 'unknown', label: localize('handshake.player.unknown'), count: unknown },
  ].filter((part) => part.count > 0);
});

/** The position road names the same handshakes the people road leaves uncounted. */
const countedAnonymousParts = notCountedParts;

/** Passersby and unknown sit above the line, then the ones marked unique. Usernames follow. */
const peopleList = computed(() => {
  const groups = [
    handshakeRows.value.filter((row) => row.kind === 'passerby' || row.kind === 'unknown'),
    handshakeRows.value.filter((row) => row.kind === 'reported'),
    handshakeRows.value.filter((row) => row.kind === 'account'),
  ].filter((group) => group.length);
  const entries: Array<{ key: string; row?: (typeof handshakeRows.value)[number] }> = [];

  groups.forEach((group, index) => {
    if (index > 0) entries.push({ key: `split-${index}` });
    for (const row of group) entries.push({ key: row.id, row });
  });

  return entries;
});

/** Each number once, with every other player it was checked against. */
const collection = computed(() => collectPositions(props.handshakes, props.userId));
const positions = computed(() => collection.value.played);

/** The same count as pos on the profile: every number checked on a confirmed handshake. */
const playedCount = computed(
  () =>
    new Set(props.handshakes.flatMap((handshake) => (handshake.isConfirmed ? (handshake.positions ?? []) : []))).size,
);

/** Every person, including a meeting with no position. Same idea as playedCount. */
const peopleTotal = computed(() => collection.value.peopleTotal);

const uniquePositions = computed(() => collection.value.uniqueCount);
const uniquePeople = computed(() => collection.value.people.unique + collection.value.people.reported);
const uniquePairs = computed(() => collection.value.pairCount);

/** "960 × 960", with the crown standing in for the ×. */
const collectionTitle = computed(() =>
  localize('handshake.collection.title')
    .split('×')
    .map((part) => part.trim()),
);

/** Eight places on the pair scale. The next mark opens on the pair after the last one. */
const pairMilestones = computed(() => {
  const count = uniquePairs.value;
  return BOARD_STAGES.map((value, index) => {
    const previous = index === 0 ? 0 : BOARD_STAGES[index - 1];
    const reached = count >= value;
    const opened = count >= previous + 1;
    const span = value - previous - 1;
    const body = !opened || span <= 0 ? 0 : Math.min(1, (count - previous - 1) / span);

    return {
      value,
      reached,
      foot: opened && !reached,
      body,
    };
  });
});

/** Newest meeting first, the one most likely to be remembered. */
const standInCandidates = computed(() => (isOwner.value ? [...collection.value.standInCandidates].reverse() : []));
const unknownReviewOpen = ref(false);

const imageByUsername = computed(() => {
  const map = new Map<string, string | null>();
  for (const row of handshakeRows.value) {
    if (row.username) map.set(row.username, row.image ?? null);
  }
  return map;
});

function pairImage(username: string | null | undefined): string | null {
  if (!username) return null;
  return imageByUsername.value.get(username) ?? null;
}

/** Newest unique pair first. The hero is the first. */
const uniquePairRows = computed(() => {
  const unique = collection.value.played.filter(
    (position) =>
      (position.tone === 'unique' && position.uniqueUsername) ||
      (position.tone === 'reported' && position.uniqueStandIn !== null),
  );

  const byIndex = new Map(unique.map((position) => [position.index, position]));
  const seen = new Set<number>();
  const rows: {
    index: number;
    username: string | null;
    standIn: StandInMark | null;
    image: string | null;
    createdAt: string;
    timezone: string;
  }[] = [];
  const newestFirst = [...props.handshakes].sort(
    (left, right) => right.createdAt.localeCompare(left.createdAt) || right.id.localeCompare(left.id),
  );

  for (const handshake of newestFirst) {
    for (const index of handshake.positions ?? []) {
      if (seen.has(index)) continue;
      const position = byIndex.get(index);
      if (!position) continue;
      seen.add(index);
      rows.push({
        index,
        username: position.uniqueUsername,
        standIn: position.uniqueStandIn,
        image: pairImage(position.uniqueUsername),
        createdAt: handshake.createdAt,
        timezone: handshake.timezone || 'UTC',
      });
    }
  }

  return rows;
});

/** A pinned mini, or the pair the tour is on. Nothing pinned and no pairs stays empty. */
const highlightedPair = computed(() => {
  const rows = uniquePairRows.value;
  if (!rows.length) return null;

  const pinned = selectedPairIndex.value;
  if (pinned != null) return rows.find((row) => row.index === pinned) ?? rows[0];

  return rows[cycleIndex.value % rows.length];
});

const highlightedPairMonth = computed(() =>
  highlightedPair.value ? formatMonthLabel(highlightedPair.value.createdAt, highlightedPair.value.timezone) : '',
);

/** Newest reads Latest pair. Older pairs count down from there, so the oldest is 1st pair. */
const highlightedPairLabel = computed(() => {
  const rows = uniquePairRows.value;
  const pair = highlightedPair.value;
  if (!pair || !rows.length) return '';

  const index = rows.findIndex((row) => row.index === pair.index);
  if (index <= 0) return localize('handshake.collection.recently');

  return pairOrdinal(rows.length - index);
});

const pairOrdinal = (place: number) => {
  const mod100 = place % 100;
  const mod10 = place % 10;
  const key =
    mod100 >= 11 && mod100 <= 13
      ? 'handshake.collection.ordinal.th'
      : mod10 === 1
        ? 'handshake.collection.ordinal.st'
        : mod10 === 2
          ? 'handshake.collection.ordinal.nd'
          : mod10 === 3
            ? 'handshake.collection.ordinal.rd'
            : 'handshake.collection.ordinal.th';

  return localizeWithValues(key, [String(place)]);
};

const highlightedPosition = computed(() => {
  const pair = highlightedPair.value;
  if (!pair) return null;

  return collection.value.played.find((position) => position.index === pair.index) ?? null;
});

const highlightedPhotos = computed(() => highlightedPosition.value?.photos ?? []);

function showPair(index: number) {
  clearPairTour();
  selectedPairIndex.value = index;
}

/** The count is the tour. A pinned mini stays until this starts it again. */
function playPairs() {
  stopPairFade();
  selectedPairIndex.value = null;
  cycleIndex.value = 0;
}

const PAIR_TOUR_MS = 2200;
/** Slower than the auth shuffle. The next pair fades in over the one already showing. */
const PAIR_FADE_MS = 780;

/** Two or more pairs, and nothing pinned, so the featured pair can move on. */
const pairCycling = computed(
  () =>
    props.visible &&
    !boardOpen.value &&
    activeTab.value === 'pairs' &&
    selectedPairIndex.value == null &&
    uniquePairRows.value.length > 1,
);

/** The lines stay for one pair, no pairs, or a pinned mini. The tour is only the face. */
const pairLinesShown = computed(() => selectedPairIndex.value != null || uniquePairRows.value.length < 2);

type FeaturedPair = (typeof uniquePairRows.value)[number];

interface FeaturedLayer {
  id: number;
  pair: FeaturedPair;
}

/** The pair on show, and the next one dissolving over it. */
const pairLayers = ref<FeaturedLayer[]>([]);
const enteringPairId = ref<number | null>(null);
let pairLayerSeq = 0;
const pairLayerEls = new Map<number, HTMLElement>();

function bindPairLayer(id: number, el: unknown) {
  if (el instanceof HTMLElement) pairLayerEls.set(id, el);
  else pairLayerEls.delete(id);
}

watch(
  () => highlightedPair.value?.index ?? null,
  () => {
    if (pairFade || pairCrossfading) return;
    const pair = highlightedPair.value;
    enteringPairId.value = null;
    pairLayers.value = pair ? [{ id: ++pairLayerSeq, pair }] : [];
  },
  { immediate: true },
);

function advancePair() {
  if (!pairCycling.value || pairFade || pairCrossfading) return;
  const rows = uniquePairRows.value;
  const current = pairLayers.value[pairLayers.value.length - 1];
  if (!current || rows.length < 2) return;

  const total = rows.length;
  const next = (cycleIndex.value + 1) % total;
  const incoming: FeaturedLayer = { id: ++pairLayerSeq, pair: rows[next] };
  // The mini ring moves with the pair that is arriving, not after it has settled.
  pairCrossfading = true;
  cycleIndex.value = next;
  enteringPairId.value = incoming.id;
  pairLayers.value = [current, incoming];

  void nextTick().then(() => {
    if (!pairCrossfading) return;
    const mark = pairLayerEls.get(incoming.id);
    if (!mark || !pairCycling.value) {
      enteringPairId.value = null;
      pairLayers.value = [incoming];
      pairCrossfading = false;
      return;
    }

    const fadeIn = mark.animate([{ opacity: 0 }, { opacity: 1 }], {
      duration: PAIR_FADE_MS,
      easing: 'ease-in-out',
      fill: 'forwards',
    });
    pairFade = fadeIn;

    return fadeIn.finished
      .then(async () => {
        if (pairFade !== fadeIn) return;
        mark.style.opacity = '1';
        enteringPairId.value = null;
        await nextTick();
        if (pairFade !== fadeIn) return;
        fadeIn.cancel();
        mark.style.opacity = '';
        pairLayers.value = [incoming];
        pairFade = null;
        pairCrossfading = false;
      })
      .catch(() => {
        if (pairFade === fadeIn) {
          pairFade = null;
          pairCrossfading = false;
        }
      });
  });
}

watch(
  pairCycling,
  (cycling) => {
    clearPairTour();
    if (!cycling) return;
    pairCycleTimer = window.setInterval(advancePair, PAIR_TOUR_MS);
  },
  { immediate: true },
);

onUnmounted(clearPairTour);

/** First row stays as empty minis until each slot is a real pair. No extra placeholder rows. */
const PAIR_MINI_ROW = 4;
const pairMinis = computed(() => {
  const rows = uniquePairRows.value;
  const slots = Math.max(PAIR_MINI_ROW, rows.length);

  return Array.from({ length: slots }, (_, index) => ({
    key: rows[index] ? `pair-${rows[index].index}` : `empty-${index}`,
    pair: rows[index] ?? null,
  }));
});

const isMaster = computed(() => uniquePositions.value >= 960 && uniquePeople.value >= 960);

const showingRoad = computed(
  () =>
    (activeTab.value === 'positions' && positionView.value === 'road') ||
    (activeTab.value === 'people' && peopleView.value === 'road'),
);

const toggleRoad = () => {
  roadOpen.value = !roadOpen.value;
  rememberRoad(roadOpen.value);
};

type RoadPhase = 'ahead' | 'zero' | 'walk' | 'done';

/** How far a mark has filled. The span starts at the previous mark, so 1 pair fills a1 and leaves 29 empty. */
const spanPhase = (count: number, mark: number): { phase: RoadPhase; fill: number } => {
  const index = BOARD_STAGES.indexOf(mark as (typeof BOARD_STAGES)[number]);
  const previous = index <= 0 ? 0 : BOARD_STAGES[index - 1];
  if (count < previous) return { phase: 'ahead', fill: 0 };
  if (count >= mark) return { phase: 'done', fill: 1 };

  const fill = (count - previous) / (mark - previous);
  return fill <= 0 ? { phase: 'zero', fill: 0 } : { phase: 'walk', fill };
};

/**
 * Share of 960, rounded down so it never says 100 with a position still missing.
 * Under 10% keeps one decimal, so a handful of games still shows as more than 0.
 */
const percentOf960 = (count: number): string => {
  const percent = (Math.min(count, 960) / 960) * 100;
  if (percent >= 10) return String(Math.floor(percent));

  const tenth = Math.floor(percent * 10) / 10;
  return Number.isInteger(tenth) ? String(tenth) : tenth.toFixed(1);
};

const roadKind = computed<'pairs' | 'positions' | 'people'>(() => {
  if (boardOpen.value) return 'pairs';
  return activeTab.value === 'people' ? 'people' : 'positions';
});

const roadCount = computed(() => {
  if (roadKind.value === 'pairs') return uniquePairs.value;
  if (roadKind.value === 'people') return peopleTotal.value;
  return playedCount.value;
});

type RoadTone = 'ahead' | 'unique' | 'repeat' | 'unknown';

/** Pairs: pink or black. Positions and people: white once the count has hit the mark. */
const roadTone = (mark: number, total: number, pairs: boolean): RoadTone => {
  if (total < mark) return 'ahead';
  return pairs ? 'unique' : 'repeat';
};

const roadNodes = computed(() => {
  const kind = roadKind.value;
  const count = roadCount.value;
  const nextIndex = BOARD_STAGES.findIndex((mark) => count < mark);
  const nodes: {
    key: string;
    value: string;
    notation: string;
    phase: RoadPhase;
    tone: RoadTone;
    gained: number;
    missing: number;
    nextMark: boolean;
    nextChip: boolean;
    line: 'ahead' | 'walk' | 'done';
    walk: number;
  }[] = [
    {
      key: 'start',
      value: '',
      notation: localize('handshake.collection.milestone.start'),
      phase: 'done',
      tone: 'repeat',
      gained: 0,
      missing: 0,
      nextMark: true,
      nextChip: false,
      line: 'ahead',
      walk: 0,
    },
  ];

  BOARD_STAGES.forEach((mark, index) => {
    const span = spanPhase(count, mark);
    nodes.push({
      key: String(mark),
      value: String(mark),
      notation:
        kind === 'pairs'
          ? `${BOARD_FILES[index]}${index + 1}`
          : kind === 'positions'
            ? BOARD_FILES[index]
            : String(index + 1),
      phase: span.phase,
      tone: roadTone(mark, count, kind === 'pairs'),
      gained: 0,
      missing: 0,
      nextMark: false,
      nextChip: false,
      line: 'ahead',
      walk: 0,
    });
  });

  if (nextIndex >= 0) {
    const nextMark = BOARD_STAGES[nextIndex];
    const previous = nextIndex <= 0 ? 0 : BOARD_STAGES[nextIndex - 1];
    const gained = count - previous;
    const missing = nextMark - count;
    // On an exact milestone the mark is a white dot. One step past, it becomes + and the next becomes −.
    if (gained > 0) {
      nodes[nextIndex].gained = gained;
      if (missing > 0) nodes[nextIndex + 1].missing = missing;
    }
  }

  const spanStart = nextIndex <= 0 ? 0 : BOARD_STAGES[nextIndex - 1];
  const walked = nextIndex < 0 ? 1 : (count - spanStart) / (BOARD_STAGES[nextIndex] - spanStart);

  for (let index = 0; index < nodes.length - 1; index++) {
    nodes[index].nextMark = true;
    nodes[index].nextChip = nodes[index + 1].gained > 0 || nodes[index + 1].missing > 0;
    if (nextIndex < 0 || index < nextIndex) nodes[index].line = 'done';
    else if (index === nextIndex) {
      nodes[index].line = 'walk';
      nodes[index].walk = Math.round(walked * 1000) / 10;
    }
  }

  return nodes;
});

/**
 * Between milestones, the count sits on the white dot in the gap.
 * On an exact milestone that dot is the milestone, so only the number is drawn above it.
 */
const roadYou = computed(() => {
  const count = roadCount.value;
  const nextIndex = BOARD_STAGES.findIndex((mark) => count < mark);
  const hit = BOARD_STAGES.findIndex((mark) => mark === count);
  const onMark = count === 0 || hit >= 0 || nextIndex < 0;

  if (onMark) {
    const fromNode = count === 0 ? 0 : nextIndex < 0 ? BOARD_STAGES.length : hit + 1;
    return {
      count,
      fromNode,
      span: 0,
      progress: 0,
      fromChip: false,
      toChip: false,
      dot: false,
    };
  }

  const previous = nextIndex <= 0 ? 0 : BOARD_STAGES[nextIndex - 1];
  const nextMark = BOARD_STAGES[nextIndex];
  return {
    count,
    fromNode: nextIndex,
    span: 1,
    progress: (count - previous) / (nextMark - previous),
    fromChip: nextIndex > 0,
    toChip: true,
    dot: true,
  };
});

const roadYouStyle = computed(() => {
  const you = roadYou.value;
  const radius = (chip: boolean) => (you.span === 0 ? '0px' : chip ? '0.75rem' : '4px');
  return {
    '--from': String(you.fromNode),
    '--span': String(you.span),
    '--progress': String(you.progress),
    '--from-r': radius(you.fromChip),
    '--to-r': radius(you.toChip),
  };
});

/**
 * Where the collection sits. The diagonal is pairs. People ahead of that square
 * take the ranks above it, positions the files beyond it. People must be
 * strictly further. A tie stays with positions. One side alone keeps a single
 * token, so people read 1 and positions read a, and a1 stays the pair square.
 */
const headerMark = computed(() => {
  const pairIndex = stageIndex(uniquePairs.value);
  const anchor = Math.max(pairIndex, 0);
  const fileIndex = stageIndex(playedCount.value);
  const rankIndex = stageIndex(peopleTotal.value);
  const peopleLead = rankIndex > pairIndex && rankIndex > fileIndex;
  const positionsLead = fileIndex > pairIndex && fileIndex >= rankIndex;

  if (peopleLead) {
    const onOrigin = rankIndex === 0 && pairIndex < 0;
    if (playedCount.value === 0 || onOrigin) return { text: String(rankIndex + 1), tone: 'axis' as const };
    return { text: `${BOARD_FILES[anchor]}${rankIndex + 1}`, tone: 'axis' as const };
  }

  if (positionsLead) {
    const onOrigin = fileIndex === 0 && pairIndex < 0;
    if (peopleTotal.value === 0 || onOrigin) return { text: BOARD_FILES[fileIndex], tone: 'axis' as const };
    return { text: `${BOARD_FILES[fileIndex]}${anchor + 1}`, tone: 'axis' as const };
  }

  if (pairIndex >= 0) {
    return { text: `${BOARD_FILES[pairIndex]}${pairIndex + 1}`, tone: 'pair' as const };
  }

  return { text: '', tone: 'empty' as const };
});

/** How far unique pairs have filled a diagonal square. The span starts at the previous mark. */
const pairFill = (target: number) => {
  const index = BOARD_STAGES.indexOf(target as (typeof BOARD_STAGES)[number]);
  const previous = index <= 0 ? 0 : BOARD_STAGES[index - 1];
  const pairs = uniquePairs.value;
  if (pairs <= previous) return 0;
  if (pairs >= target) return 1;

  return (pairs - previous) / (target - previous);
};

const boardSquares = computed(() => {
  const squares: { key: string; fill: number; axis: boolean; isFirstFile: boolean; isTopRank: boolean }[] = [];
  const pairIndex = stageIndex(uniquePairs.value);
  const anchor = Math.max(pairIndex, 0);
  const walking = pairIndex < 0 ? 0 : anchor + 1;
  const peopleSquares = playedCount.value >= 1;
  const positionSquares = peopleTotal.value >= 1;

  for (let rank = 8; rank >= 1; rank--) {
    for (let file = 0; file < 8; file++) {
      const rankIndex = rank - 1;
      const onDiagonal = file === rankIndex;
      let fill = 0;
      let axis = false;

      if (onDiagonal && pairIndex >= 0 && file <= pairIndex) {
        fill = 1;
      } else if (onDiagonal && file === walking && walking < BOARD_STAGES.length) {
        fill = pairFill(BOARD_STAGES[file]);
      } else if (peopleSquares && file === anchor && rankIndex > anchor) {
        fill = spanPhase(peopleTotal.value, BOARD_STAGES[rankIndex]).fill;
        axis = fill > 0;
      } else if (positionSquares && rankIndex === anchor && file > anchor) {
        fill = spanPhase(playedCount.value, BOARD_STAGES[file]).fill;
        axis = fill > 0;
      }

      squares.push({
        key: `${BOARD_FILES[file]}${rank}`,
        fill,
        axis,
        isFirstFile: file === 0,
        isTopRank: rank === 8,
      });
    }
  }

  return squares;
});

/** Gray dots on one axis, used while the other side has not arrived to open a square. */
const axisDots = (count: number, show: boolean) =>
  BOARD_STAGES.map((mark) => {
    const fill = show ? spanPhase(count, mark).fill : 0;
    return { mark, fill, dot: fill > 0 };
  });

const fileAxis = computed(() => axisDots(playedCount.value, peopleTotal.value === 0));
const rankAxis = computed(() => [...axisDots(peopleTotal.value, playedCount.value === 0)].reverse());

const uniqueRemaining = (unique: number) => (unique >= 960 ? null : 960 - unique);

const overviewTitleKey = computed(
  () => `handshake.collection.tabs.${activeTab.value === 'people' ? 'people' : 'positions'}`,
);

const overviewChip = computed(() => {
  if (isMaster.value || roadKind.value === 'pairs') return null;

  if (roadKind.value === 'people') {
    if (peopleTotal.value < 960) return null;
    const left = uniqueRemaining(uniquePositions.value);
    return left == null ? null : { left, kind: 'positions' as const };
  }

  if (playedCount.value < 960) return null;
  const left = uniqueRemaining(uniquePeople.value);
  return left == null ? null : { left, kind: 'people' as const };
});

const uniqueChipLabel = (gap: { left: number; kind: 'positions' | 'people' }) => {
  if (gap.kind === 'people') {
    return gap.left === 1
      ? 'handshake.collection.nextMilestone.uniquePerson'
      : 'handshake.collection.nextMilestone.uniquePeople';
  }

  return gap.left === 1
    ? 'handshake.collection.nextMilestone.uniquePosition'
    : 'handshake.collection.nextMilestone.uniquePositions';
};

const positionCounts = computed(() => {
  const counts = { unique: 0, reported: 0, repeat: 0, unknown: 0 };
  for (const tone of collection.value.tones) {
    if (tone !== 'empty') counts[tone] += 1;
  }

  return counts;
});

/**
 * The people ring is the same goal as the positions ring: 960. Unique comes
 * first, then repeated, and no-position fills only what is left of that 960.
 * People past 960, who have no position, are not drawn. They cannot move the
 * ring any closer to the goal or any further from it.
 */
const peopleRingCounts = computed(() => {
  const { unique, reported, repeat, none } = collection.value.people;
  const goal = POSITION_INDEXES.length;
  const coloredUnique = Math.min(unique, goal);
  const coloredReported = Math.min(reported, goal - coloredUnique);
  const coloredRepeat = Math.min(repeat, goal - coloredUnique - coloredReported);
  const coloredNone = Math.min(none, goal - coloredUnique - coloredReported - coloredRepeat);

  return { unique: coloredUnique, reported: coloredReported, repeat: coloredRepeat, none: coloredNone };
});

const peopleRingTotal = computed(
  () =>
    peopleRingCounts.value.unique +
    peopleRingCounts.value.reported +
    peopleRingCounts.value.repeat +
    peopleRingCounts.value.none,
);

/**
 * Arcs for one ring. `whole` is the 960 the circle stands for. A group with
 * someone in it stays visible when its share would be thinner than a pixel.
 */
const ringArcs = (counts: Record<string, number>, whole: number, order: readonly string[]) => {
  const active = order.filter((tone) => counts[tone] > 0);
  if (!active.length || whole <= 0) return [];

  const arcs = active.map((tone) => ({
    tone,
    length: (counts[tone] / whole) * DONUT_CIRCUMFERENCE,
  }));
  const colored = arcs.reduce((sum, arc) => sum + arc.length, 0);

  for (const arc of arcs) {
    if (arc.length < MIN_ARC) arc.length = MIN_ARC;
  }

  let extra = arcs.reduce((sum, arc) => sum + arc.length, 0) - colored;
  const donors = arcs.filter((arc) => arc.length > MIN_ARC).sort((left, right) => right.length - left.length);
  for (const donor of donors) {
    if (extra <= 0) break;

    const take = Math.min(donor.length - MIN_ARC, extra);
    donor.length -= take;
    extra -= take;
  }

  let cursor = 0;

  return arcs.map((arc, index) => {
    const start = cursor;
    cursor += arc.length;
    const drawn = index < arcs.length - 1 ? arc.length + DONUT_OVERLAP : arc.length;

    return { tone: arc.tone, dash: `${drawn} ${DONUT_CIRCUMFERENCE}`, offset: -start };
  });
};

/** From the top, clockwise. The rest of the position ring is the empty track. */
const positionArcs = computed(() => ringArcs(positionCounts.value, POSITION_INDEXES.length, POSITION_TONES));

/** The whole circle is 960 people. What is past that stays off the ring. */
const peopleArcs = computed(() => ringArcs(peopleRingCounts.value, POSITION_INDEXES.length, PEOPLE_TONES));

const selected = computed(() => {
  if (selectedIndex.value == null) return null;

  return (
    collection.value.played.find((position) => position.index === selectedIndex.value) ?? {
      index: selectedIndex.value,
      usernames: [],
      standIns: [],
      uniqueUsername: null,
      uniqueStandIn: null,
      anonymous: [],
      photos: [],
      tone: 'empty' as const,
    }
  );
});

/** The cell's colour, in one phrase. An unplayed square has nothing to name. */
const selectedToneLabel = computed(() => {
  const tone = selected.value?.tone;
  if (!tone || tone === 'empty') return '';

  return localize(`handshake.collection.tone.${tone}`);
});

function getImageSrc(hash: string): string {
  return gambits.imageService.getImageSrc(hash, 100);
}

/** Opening a photo pushes a history entry. Closing has to undo that, or the profile's X steps back through it. */
const pushedPhoto = ref(false);

/** Pink, white, or gray, the same colours the handshake gallery uses in place of the board. */
function positionToneFor(index: number | null, handshake?: HandshakeDto): 'unique' | 'reported' | 'repeat' | 'unknown' {
  if (index == null) return 'unknown';

  const tone = collection.value.tones[index];
  if (tone && tone !== 'empty') return tone;

  return handshake?.initiator?.username && handshake?.responder?.username ? 'repeat' : 'unknown';
}

function imagesForPosition(position: { index: number; photos: CollectedPhoto[] }) {
  return position.photos.map((photo) => {
    const handshake = props.handshakes.find((item) => item.id === photo.handshakeId);
    const canAssign =
      !!user.value &&
      !!handshake &&
      (handshake.initiatorId === user.value.id || handshake.responderId === user.value.id);

    return {
      id: `${photo.handshakeId}:${photo.hash}`,
      imageBlobHash: photo.hash,
      liked: false,
      user: photo.user,
      createdAt: handshake?.createdAt,
      timezone: handshake?.timezone || 'UTC',
      handshakeId: photo.handshakeId,
      positionIndex: position.index,
      canAssignPosition: canAssign,
      positionTone: positionToneFor(position.index, handshake),
    };
  });
}

function openHighlightedPhoto() {
  const position = highlightedPosition.value;
  const photo = highlightedPhotos.value[0];
  if (!position || !photo) return;

  openPositionPhoto(position, photo.hash);
}

/** The same file can be on two handshakes under one hash, so a known handshake picks the right copy. */
function openPositionPhoto(position: { index: number; photos: CollectedPhoto[] }, hash: string, handshakeId?: string) {
  const images = imagesForPosition(position);
  const imageIndex = images.findIndex(
    (image) => image.imageBlobHash === hash && (!handshakeId || image.handshakeId === handshakeId),
  );
  if (imageIndex === -1) return;

  galleryPosition.value = position.index;
  selectedImage.value = {
    blobSource: BlobSource.HANDSHAKE,
    selectedIndex: imageIndex,
    images,
    noFilter: true,
  };

  pushedPhoto.value = true;
  router.push({ ...route, query: { ...route.query, photo: images[imageIndex].id } });
  nextTick(() => {
    imageExpand.value?.show();
  });
}

function openReviewPhoto(index: number, hash: string, handshakeId: string) {
  const position = collection.value.played.find((item) => item.index === index);
  if (position) openPositionPhoto(position, hash, handshakeId);
}

function closePositionPhoto() {
  selectedImage.value = null;
  galleryPosition.value = null;
  if (pushedPhoto.value && route.query.photo) {
    pushedPhoto.value = false;
    router.back();
    return;
  }

  pushedPhoto.value = false;
  if (!route.query.photo) return;

  router.replace({ ...route, query: { ...route.query, photo: undefined } });
}

function openPhotoPosition() {
  const image = galleryImage.value;
  if (!image?.handshakeId || !image.canAssignPosition) return;

  const handshake = props.handshakes.find((item) => item.id === image.handshakeId);
  if (!handshake) return;

  const ownerId = image.user?.id ?? null;
  photoPosition.value = {
    handshakeId: handshake.id,
    hash: image.imageBlobHash,
    ownerId,
    positionIndex: image.positionIndex ?? null,
    positions: handshake.positions ?? [],
    taken: positionsAtPhotoLimit(handshake.photos ?? [], ownerId, image.imageBlobHash),
    meetingFull: photosInSlot(handshake, ownerId, null, image.imageBlobHash) >= 5,
  };
}

function rememberMovedPhoto(positionIndex: number | null) {
  const target = photoPosition.value;
  if (!target) return;

  const handshake = props.handshakes.find((item) => item.id === target.handshakeId);
  const photo = handshake?.photos?.find((item) => item.imageBlobHash === target.hash && item.userId === target.ownerId);
  if (photo) photo.positionIndex = positionIndex;

  const shown = selectedImage.value?.images.find((item) => item.imageBlobHash === target.hash);
  if (shown) {
    shown.positionIndex = positionIndex;
    shown.positionTone = positionToneFor(positionIndex, handshake);
  }

  photoPosition.value = {
    ...target,
    positionIndex,
    taken: positionsAtPhotoLimit(handshake?.photos ?? [], target.ownerId, target.hash),
    meetingFull: photosInSlot(handshake ?? {}, target.ownerId, null, target.hash) >= 5,
  };
}

/**
 * The photo on screen. The URL names it by its id; a bare hash is an older link,
 * and the same file can sit on two handshakes under one hash.
 */
const galleryImage = computed(() => {
  if (!selectedImage.value) return null;

  const photo = route.query.photo;
  if (typeof photo === 'string') {
    const images = selectedImage.value.images;
    const match = images.find((item) => item.id === photo) ?? images.find((item) => item.imageBlobHash === photo);
    if (match) return match;
  }

  return selectedImage.value.images[selectedImage.value.selectedIndex] ?? null;
});

const canAddGalleryPhoto = computed(() => {
  const image = galleryImage.value;
  if (!image?.handshakeId || !user.value || galleryPosition.value == null) return false;

  const handshake = props.handshakes.find((item) => item.id === image.handshakeId);
  if (!handshake) return false;
  if (handshake.initiatorId !== user.value.id && handshake.responderId !== user.value.id) return false;

  return photosInSlot(handshake, user.value.id, galleryPosition.value) < 5;
});

/** The image icon only while this handshake is still the open one. The menu keeps Add after it closes. */
const galleryAddShortcut = computed(() => {
  if (!canAddGalleryPhoto.value) return false;

  const handshake = props.handshakes.find((item) => item.id === galleryImage.value?.handshakeId);
  if (!handshake) return false;

  return isOpenHandshake(handshake, props.handshakes, user.value?.id, now.value);
});

function addGalleryPhoto() {
  const image = galleryImage.value;
  if (!image?.handshakeId || galleryPosition.value == null) return;

  if (!canAddGalleryPhoto.value) {
    toast.error(localize('handshake.photo.taken'));
    return;
  }

  galleryUpload.value = { handshakeId: image.handshakeId, positionIndex: galleryPosition.value };
  openGalleryFile();
}

async function deleteGalleryPhoto(hash: string) {
  const image = galleryImage.value;
  if (!image?.handshakeId || image.imageBlobHash !== hash) return;

  const handshakeId = image.handshakeId;
  const result = await gambits.userService.removeHandshakeImage(handshakeId, hash);
  if (result.error) {
    toast.error(result.error.message || localize('handshake.photo.deleteFailed'));
    return;
  }

  const handshake = props.handshakes.find((item) => item.id === handshakeId);
  if (handshake?.photos) handshake.photos = handshake.photos.filter((photo) => photo.imageBlobHash !== hash);
  settlePhotos(handshakeId);
  emit('update');
}

watch(collection, () => {
  const positionIndex = galleryPosition.value;
  if (positionIndex == null || !selectedImage.value) return;

  const position = collection.value.played.find((item) => item.index === positionIndex);
  if (!position) return;

  const images = imagesForPosition(position);
  const photo = route.query.photo;
  const byId = typeof photo === 'string' ? images.findIndex((item) => item.id === photo) : -1;
  const index = byId >= 0 ? byId : images.findIndex((item) => item.imageBlobHash === photo);

  selectedImage.value = {
    ...selectedImage.value,
    images,
    selectedIndex: index >= 0 ? index : selectedImage.value.selectedIndex,
  };
});

function applyStandInVerdict(saved: HandshakeDto) {
  const handshake = props.handshakes.find((item) => item.id === saved.id);
  if (handshake) {
    handshake.standInVerdict = saved.standInVerdict ?? null;
    handshake.standInName = saved.standInName ?? null;
  }
  if (!standInCandidates.value.length) unknownReviewOpen.value = false;
  emit('update');
}

function openPlayer(username: string) {
  void rememberCollectionTab(activeTab.value).then(() => {
    void router.push({
      name: RouterNameType.USERNAME,
      params: { username },
      query: { back: 'history' },
    });
  });
}

/** A fresh clock, with the recommendation already open on this square. */
function playPosition() {
  const index = selectedIndex.value;
  if (index == null) return;

  emit('close');
  void router.push({
    name: RouterNameType.CLOCK,
    query: { pos: String(index), sheet: '1' },
  });
}
</script>

<style lang="scss" scoped>
@use '@/ui/css/mixins' as *;

/* The followers overlay's frame, so both open at the same size and scroll inside. */
.CollectionOverlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Head circle ends at 4.98 / 16 of the pawn. That part hangs above the drawer. */
  --pawn-size: 36px;
  --pawn-overhang: calc(var(--pawn-size) * 0.3115 + 2px);
  /* Room for the centered title, then the pawn heads, then the drawer. */
  --brand-clearance: calc(env(safe-area-inset-top, 0px) + 0.85rem + 30px + 0.85rem + var(--pawn-overhang));
  padding-top: var(--brand-clearance);

  /* Its own layer, so the sphere bloom can fade the dim without fading the drawer. */
  &::before {
    content: '';
    position: absolute;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
  }
}

.collection-wrapper {
  position: relative;
  box-sizing: border-box;
  width: min(40rem, calc(100vw - 2rem));
  max-width: calc(100vw - 2rem);
  height: min(700px, calc(100vh - var(--brand-clearance) - 2rem));
  /* The visible screen: a mobile browser's vh assumes its toolbars are hidden, which pushes the drawer's top, and the face above it, off screen. */
  height: min(700px, calc(100dvh - var(--brand-clearance) - 2rem));
  display: flex;
  flex-direction: column;

  &.is-mobile {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    max-width: 100%;
    height: 85vh;
    height: 85dvh;

    .collection-card {
      border-radius: 1.5rem 1.5rem 0 0;
    }
  }
}

.collection-card {
  position: relative;
  flex: 1;
  min-height: 0;
  border-radius: 1.5rem;
  border: 1px solid var(--color-white-10);
  background: var(--color-dark-700);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  will-change: transform;
}

.collection-scroll {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  touch-action: pan-y;
  scrollbar-width: none;

  &::-webkit-scrollbar {
    display: none;
  }
}

.collection-heading {
  flex-shrink: 0;
  /* The pawn body, the number under it, and a little air. The head is the overhang above. */
  height: calc(var(--pawn-size) - var(--pawn-overhang) + 0.4rem + 10px + 0.7rem);
  /* The pair scale. The same band heads every tab. */
  background: radial-gradient(80% 55% at 50% 0%, rgba(255, 255, 255, 0.05), transparent 70%), var(--color-dark-600);
}

.pair-milestones {
  position: absolute;
  top: 0;
  right: 0;
  left: 0;
  z-index: 3;
  display: grid;
  grid-template-columns: repeat(8, minmax(0, 1fr));
  box-sizing: border-box;
  width: 100%;
  margin: 0;
  padding: 0 var(--spacing-l);
  list-style: none;
  pointer-events: none;
  transform: translateY(calc(-1 * var(--pawn-overhang)));
}

.pair-milestone {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  color: var(--color-white-50);
}

.pair-milestone-mark {
  position: relative;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  width: 100%;
  height: var(--pawn-size);
  color: var(--color-white-50);
}

/* The pawn's own foot is the bar. This continues it from one pawn to the next. */
.pair-milestone-mark::before {
  content: '';
  position: absolute;
  top: 87.3%;
  right: 0;
  left: 0;
  z-index: 0;
  height: 1px;
  background: var(--color-white-30);
  transform: translateY(-50%);
}

.pair-milestone:first-child .pair-milestone-mark::before {
  left: 50%;
}

.pair-milestone:last-child .pair-milestone-mark::before {
  right: 50%;
}

.pair-milestone-mark :deep(.svg-container) {
  position: relative;
  z-index: 1;
}

/* The foot bar is the whole bottom of the icon. Color climbs from there. */
.pair-milestone-fill {
  position: absolute;
  inset: 0;
  z-index: 2;
  display: flex;
  align-items: flex-end;
  justify-content: center;
  color: var(--color-pink);
  clip-path: inset(calc((1 - var(--pawn-rise, 0)) * 84.67%) 0 0 0);
  pointer-events: none;
}

.pair-milestone.is-reached,
.pair-milestone.is-reached .pair-milestone-mark {
  color: var(--color-pink);
}

.pair-milestone-need {
  font-size: 10px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1;
}

/* Crown and title, centered at the top of the screen. */
.collection-brand {
  position: absolute;
  top: calc(env(safe-area-inset-top, 0px) + 0.85rem);
  left: 50%;
  z-index: 5;
  --silver: linear-gradient(135deg, #8e8e8e 0%, #f4f4f4 38%, #ffffff 48%, #a9a9a9 62%, #e6e6e6 100%);
  display: flex;
  min-width: 0;
  align-items: center;
  gap: var(--spacing-s);
  padding: 0;
  border: 0;
  background: none;
  color: inherit;
  font: inherit;
  text-align: center;
  cursor: pointer;
  transform: translateX(-50%);
}

.collection-brand h2 {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
  letter-spacing: 0.02em;
}

.collection-figure {
  background: var(--silver);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}

.collection-latest {
  margin: 0.45rem 0 0;
  color: var(--color-white-40);
  font-size: 11px;
  font-weight: 500;
  line-height: 1;
  letter-spacing: 0.05em;
  text-align: center;
  white-space: nowrap;
}

/* One height either way, so the count stays put. The tour is the larger face. */
.collection-featured {
  --featured-face: 76px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  width: min(100%, 300px);
  height: 9.25rem;

  &.is-quiet {
    --featured-face: 116px;
  }
}

.featured-mark {
  display: grid;
  width: 100%;
}

/* The next pair fades in over the one on show, so the board never drops out. */
.featured-layer {
  grid-area: 1 / 1;
  z-index: 0;
  width: 100%;
  isolation: isolate;

  &.is-entering {
    z-index: 1;
    opacity: 0;
  }
}

.featured-lines {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  max-height: 0;
  overflow: hidden;
  opacity: 0;
  transition:
    max-height 400ms ease,
    opacity 400ms ease;

  &.is-shown {
    max-height: 2.75rem;
    padding-top: 0.35rem;
    opacity: 1;
  }
}

.collection-featured :deep(.HandshakePositionMark) {
  width: 100%;
}

.collection-featured :deep(.face) {
  --face-size: var(--featured-face);
  margin-bottom: -10px;
  box-shadow: 0 0 0 2px var(--color-dark-600);
  transition:
    width 400ms ease,
    height 400ms ease;
}

.collection-featured :deep(.is-empty .face) {
  background: var(--color-not-played);
  color: var(--color-white-30);
}

.collection-featured :deep(.number) {
  justify-content: center;
  min-width: 0;
  max-width: 100%;
}

.collection-featured :deep(.board) {
  container-type: inline-size;
}

/* A photo shortens the rank. Preserve the full-rank height so the caption and
   title do not move when a selected mini has a photo. */
.collection-featured :deep(.rank-line) {
  align-items: flex-end;
  min-height: calc(100cqw / 8);
}

.collection-featured :deep(.side-photo) {
  align-self: flex-end;
  box-shadow: 0 0 0 1px var(--color-white-10);
}

.featured-caption {
  width: 100%;
  max-width: 100%;
  margin: -0.1rem 0 0;
  overflow: hidden;
  color: var(--color-white-50);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.2;
  letter-spacing: 0.05em;
  text-align: center;
  text-overflow: ellipsis;
  white-space: nowrap;

  .is-unique {
    color: var(--color-pink);
  }

  .is-reported {
    color: var(--color-reported);
  }
}

/* The profile's glass, small enough to sit between the figures. One small silver glint, so the ball stays round. */
.header-master {
  --sphere-size: 30px;
  flex-shrink: 0;
  pointer-events: none;

  :deep(.circle) {
    background: radial-gradient(
        circle at var(--light-x) var(--light-y),
        rgba(255, 255, 255, 0.16) 0%,
        rgba(210, 210, 210, 0.05) 42%,
        transparent 72%
      ),
      transparent;
    border-color: rgba(255, 255, 255, 0.28);
    box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.08);
  }

  :deep(.circle::before) {
    width: 26%;
    height: 14%;
    background: radial-gradient(ellipse at center, rgba(255, 255, 255, 0.9) 0%, rgba(214, 214, 214, 0) 70%);
  }

  :deep(.circle::after) {
    display: none;
  }

  :deep(.icon-3d-wrapper) {
    width: 46%;
    height: 46%;
    color: var(--color-white);
    filter: drop-shadow(0 1px 2px rgba(0, 0, 0, 0.45));
  }
}

.header-square {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--walk-square);
  height: var(--walk-square);
  padding: 0;
  border: 0;
  background: transparent;
  color: inherit;
  font: inherit;
  box-shadow: inset 0 0 0 1px var(--color-white-20);
  cursor: pointer;

  &.is-open {
    background: var(--color-white-10);
  }

  &.is-pair {
    box-shadow: inset 0 0 0 1px var(--color-pink);
    color: var(--color-pink);
  }

  &.is-axis {
    box-shadow: inset 0 0 0 1px var(--color-white);
    color: var(--color-white);
  }
}

.header-coord {
  font-size: calc(var(--walk-square) * 0.4);
  font-weight: 700;
  line-height: 1;
}

.collection-title {
  min-width: 0;
}

/* Room under the last row for the close button that floats over the list. */
.collection-content {
  padding: var(--spacing-l);
  padding-bottom: calc(var(--safe-bottom-offset) + 7.5rem);

  &.has-lens {
    padding-bottom: calc(var(--safe-bottom-offset) + 10rem);
  }

  &:has(.stage-board) {
    flex-shrink: 0;
  }

  &.has-board {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding: 0;
  }

  &.is-pairs {
    flex: 1;
    display: flex;
    flex-direction: column;
    min-height: 0;
    padding-top: var(--spacing-md);
    padding-bottom: 0;
    /* Same gray as the heading. Positions and people stay on the darker card. */
    background: var(--color-dark-600);
  }
}

.player-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  transition: opacity var(--transition);

  &.is-link {
    cursor: pointer;

    @include hover {
      opacity: 0.7;
    }
  }
}

.player-avatar {
  width: 40px;
  height: 40px;
  border-radius: 50%;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--color-dark-500);
  flex-shrink: 0;
  color: var(--color-white);

  .picture {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
  }
}

.player-line {
  display: flex;
  align-items: baseline;
  flex: 1;
  gap: 0.75rem;
  min-width: 0;
}

.player-username {
  min-width: 0;
  overflow: hidden;
  font-size: 14px;
  font-weight: 500;
  color: var(--color-white-100);
  text-overflow: ellipsis;
  white-space: nowrap;
}

.player-row.is-unknown .player-username {
  color: var(--color-unknown);
}

/* Not an account, so not the gray of a user with no picture. */
.player-avatar.is-stand-in,
.pair-photo.is-stand-in {
  background: var(--color-not-played);
  color: var(--color-unknown);
}

.people-head {
  display: flex;
  justify-content: flex-end;
  padding-bottom: 0.15rem;
  color: var(--color-white-40);
  font-size: 11px;
  font-weight: 700;
  line-height: 1;
}

.people-split {
  height: 1px;
  margin: 0.35rem 0;
  background: var(--color-white-10);
}

.player-positions {
  margin-left: auto;
  flex-shrink: 0;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  color: var(--color-white-40);
}

.position-list {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: var(--spacing-l);
}

.collection-summary {
  display: flex;
  align-items: center;
  gap: var(--spacing-l);
  margin-bottom: var(--spacing-l);
}

/* The road is its own band. The ring starts after the gap under it. */
.overview {
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--spacing-xl);
  padding-top: var(--spacing-s);
  /* Vertical scroll stays with the page. A sideways touch switches both bands together. */
  touch-action: pan-y;
}

.road {
  position: relative;
  display: flex;
  flex-direction: column;
  align-items: stretch;
  gap: var(--spacing-l);
  /* Flush under the tabs: cancels the content and overview padding above it. */
  margin: calc((var(--spacing-l) + var(--spacing-s)) * -1) calc(var(--spacing-l) * -1) 0;
  padding: var(--spacing-xl) var(--spacing-l);
  background: color-mix(in srgb, var(--color-white) 5%, transparent);

  &.is-toggle {
    cursor: pointer;
  }
}

.road-heading {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--spacing-md);
}

.road-count {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  font-variant-numeric: tabular-nums;
  color: var(--color-white);

  &.is-pairs {
    color: var(--color-pink);
  }
}

/* As tall as the count on the other slides, so the gray band does not change height between them. */
.header-square.is-road {
  --walk-square: 28px;
}

.road-title {
  position: relative;
  margin: 0;
  text-align: center;
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.16em;
  text-transform: uppercase;
  color: var(--color-white);
}

.road-icon {
  position: absolute;
  left: 100%;
  top: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: 0.5rem;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.06);
  transform: translateY(-50%);
  color: var(--color-white-70);
  cursor: pointer;

  @include hover {
    background: rgba(255, 255, 255, 0.12);
    color: var(--color-white);
  }

  &.is-open {
    background: rgba(255, 255, 255, 0.15);
    color: var(--color-white);
  }
}

.road-marks {
  --dot-size: 8px;
  --dot-r: calc(var(--dot-size) / 2);
  --circle-size: 1.5rem;
  --label-line: 10px;
  --label-gap: 0.12rem;
  --mark-gap: 0.2rem;

  position: relative;
  display: flex;
  align-items: flex-start;
  width: 100%;
  margin-top: 1.4rem;
}

.road-node {
  position: relative;
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  gap: var(--mark-gap);
  min-width: 0;
}

.road-labels {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--label-gap);
  width: 100%;
}

.road-notation,
.road-figure {
  min-height: var(--label-line);
  color: var(--color-white-30);
  font-size: 10px;
  font-weight: 700;
  line-height: var(--label-line);
  text-align: center;
}

.road-figure {
  font-variant-numeric: tabular-nums;
}

.road-notation {
  text-transform: lowercase;

  &.is-unique {
    color: var(--color-pink);
  }

  &.is-repeat {
    color: var(--color-white);
  }

  &.is-unknown {
    color: var(--color-unknown);
  }
}

.road-mark-row {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: var(--circle-size);
}

.road-connector {
  position: absolute;
  top: 50%;
  left: calc(50% + var(--mark-r));
  width: calc(100% - var(--mark-r) - var(--next-r));
  height: 1px;
  background: var(--color-white-20);
  transform: translateY(-50%);

  &.is-done {
    background: var(--color-white);
  }

  &.is-walk {
    background: linear-gradient(to right, var(--color-white) var(--walk), var(--color-white-20) var(--walk));
  }
}

.road-mark {
  position: relative;
  z-index: 1;
  width: var(--dot-size);
  height: var(--dot-size);
  border-radius: 50%;
  background: #000;

  &.is-unique {
    background: var(--color-pink);
  }

  &.is-repeat {
    background: var(--color-white);
  }

  &.is-unknown {
    background: var(--color-unknown);
  }
}

.road-delta {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 1.5rem;
  height: 1.5rem;
  padding: 0 0.28rem;
  border-radius: 999px;
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  letter-spacing: 0;

  &.is-gained {
    color: var(--color-correct);
  }

  &.is-missing {
    color: var(--color-incorrect);
  }

  &.is-unique {
    background: color-mix(in srgb, var(--color-pink) 24%, var(--color-dark-700));
  }

  &.is-repeat {
    background: color-mix(in srgb, var(--color-white) 20%, var(--color-dark-700));
  }

  &.is-unknown,
  &.is-ahead {
    background: color-mix(in srgb, var(--color-unknown) 40%, var(--color-dark-700));
  }
}

.road-you {
  position: absolute;
  z-index: 3;
  top: calc(var(--circle-size) / 2);
  left: calc((var(--from) + 0.5) / var(--nodes) * 100%);
  width: calc(var(--span) * 100% / var(--nodes));
  height: 0;
  pointer-events: none;
}

.road-you-pin {
  position: absolute;
  top: 0;
  left: calc(var(--from-r) + (100% - var(--from-r) - var(--to-r)) * var(--progress));
  width: 0;
  height: 0;
}

.road-you-dot {
  position: absolute;
  top: 0;
  left: 0;
  width: var(--dot-size);
  height: var(--dot-size);
  border-radius: 50%;
  background: var(--color-white);
  box-shadow: 0 0 0 1px var(--color-dark-700);
  transform: translate(-50%, -50%);
}

.road-you-count {
  position: absolute;
  top: calc(-1 * (var(--circle-size) / 2 + var(--mark-gap) + var(--label-line)));
  left: 0;
  height: var(--label-line);
  color: var(--color-white);
  font-size: 10px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: var(--label-line);
  white-space: nowrap;
  transform: translateX(-50%);
}

.road-marks .now-gap {
  top: 0;
  transform: translate(-50%, -130%);
}

/* Same colours as the You chip: a light wash, and the label in the gradient. */
.now-gap .gap-text {
  background: var(--color-linear-gradient);
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  color: var(--color-pink);
}

/* Each end is as wide as its name, so the name stays inside the edge and off the line. */
.milestone-now {
  --now-node: 5.5rem;
  --now-circle: 2.65rem;

  position: relative;
  display: flex;
  justify-content: space-between;
  width: 100%;

  &.is-done {
    justify-content: center;
  }
}

.now-node {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.45rem;
  width: var(--now-node);
  flex: none;
}

/* Runs from the edge of one circle to the edge of the other. */
.now-line {
  position: absolute;
  top: calc(var(--now-circle) / 2);
  left: calc(var(--now-node) / 2 + var(--now-circle) / 2);
  right: calc(var(--now-node) / 2 + var(--now-circle) / 2);
  height: 1px;
  background: var(--color-white-30);
}

.now-name {
  width: 100%;
  text-align: center;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1.25;
  color: var(--color-white);

  &.is-start,
  &.is-next {
    color: var(--color-white-40);
  }
}

.now-gap {
  position: absolute;
  top: calc(var(--now-circle) / 2);
  left: 50%;
  z-index: 1;
  transform: translate(-50%, -50%);
  display: inline-flex;
  align-items: baseline;
  padding: 0.3rem 0.5rem;
  border: 0;
  border-radius: 999px;
  background: var(--color-linear-gradient-10), color-mix(in srgb, var(--color-white) 5%, var(--color-dark-700));
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;

  .gap-value {
    margin: 0 0.25rem;
    font-size: 13px;
    font-weight: 700;
    letter-spacing: 0;
    font-variant-numeric: tabular-nums;
  }
}

.now-progress {
  --progress: 0;
  position: absolute;
  top: calc(var(--now-circle) / 2);
  left: calc(var(--now-node) / 2 + var(--now-circle) / 2);
  right: calc(var(--now-node) / 2 + var(--now-circle) / 2);
  z-index: 2;
  height: 0;
  pointer-events: none;
}

.now-dot {
  position: absolute;
  top: 0;
  left: calc(var(--progress) * 100%);
  z-index: 2;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  transform: translate(-50%, -50%);
  background: var(--color-unknown);

  &.is-unique {
    background: var(--color-pink);
  }

  &.is-repeat {
    background: var(--color-white);
  }

  &.is-unknown {
    background: var(--color-unknown);
  }
}

.now-gained,
.now-missing {
  position: absolute;
  top: 0;
  font-size: 12px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  letter-spacing: 0;
  white-space: nowrap;
  transform: translateY(calc(-100% - 6px));
}

.now-gained {
  right: calc((1 - var(--progress)) * 100% + 8px);
  color: var(--color-correct);
}

.now-missing {
  left: calc(var(--progress) * 100% + 8px);
  color: var(--color-incorrect);
}

.overview-stage {
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
}

.donut-anchor {
  position: relative;
  display: flex;
  justify-content: center;
  width: 100%;
}

.overview-arrow {
  display: flex;
  flex: none;
  align-items: center;
  justify-content: center;
  width: 2rem;
  height: 2rem;
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-white-70);
  cursor: pointer;

  &.is-hidden {
    visibility: hidden;
    pointer-events: none;
  }

  @include press;
}

.overview-dots {
  position: absolute;
  bottom: 0;
  /* The road runs to the screen. The chevrons stay in line with the circle. */
  left: var(--spacing-l);
  z-index: 2;
  display: grid;
  grid-template-columns: 2rem 1fr 2rem;
  align-items: center;
  width: calc(100% - 2 * var(--spacing-l));
  margin: 0;
  transform: translateY(50%);
}

.overview-dot-row {
  display: flex;
  justify-content: center;
  gap: 0.5rem;
}

.overview-dot {
  width: 8px;
  height: 8px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: var(--color-white-20);
  cursor: pointer;

  &.active {
    background: var(--color-white);
  }
}

.collection-scroll:has(> .collection-content.is-pairs) {
  overflow: hidden;
}

.pair-stage {
  display: flex;
  flex: 1;
  flex-direction: column;
  align-items: center;
  width: 100%;
  min-height: 0;
  overflow: hidden;
}

.pair-summary {
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding-top: 0;
}

.pair-caption {
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  color: var(--color-white-40);

  &.is-count {
    margin: 1.15rem 0 1.5rem;
    padding: 0;
    border: 0;
    background: none;
    color: var(--color-white-40);
    font-family: inherit;
    cursor: pointer;
  }
}

.pair-count {
  font-size: 28px;
  font-weight: 700;
  line-height: 1;
  color: var(--color-pink);
}

.pair-count-rest {
  margin-left: 0.35em;
}

.pair-bond {
  display: flex;
  align-items: center;

  .pair-square {
    z-index: 1;
    margin-right: calc(var(--pair-face) * -0.28);
    box-shadow: 0 0 0 2px var(--color-dark-600);
  }

  &.is-mini {
    --pair-face: 36px;
  }

  &.is-reported .pair-square {
    background: var(--color-reported);
  }

  &.is-empty .pair-photo,
  &.is-empty .pair-square,
  .pair-square.is-empty {
    background: var(--color-not-played);
    color: var(--color-white-30);
  }
}

.pair-photo,
.pair-square {
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: var(--pair-face);
  height: var(--pair-face);
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}

.pair-photo {
  border-radius: 50%;
  background: var(--color-dark-500);
  color: var(--color-white);
}

.pair-square {
  border-radius: calc(var(--pair-face) * 0.12);
  background: var(--color-pink);
  color: var(--color-white);
  padding: 0 0.12em;
  font-size: calc(var(--pair-face) * 0.24);
  font-weight: 700;
  font-variant-numeric: tabular-nums;

  .is-mini & {
    font-size: 9px;
  }
}

.pair-minis {
  display: grid;
  flex: 1;
  grid-template-columns: repeat(4, max-content);
  align-content: start;
  justify-content: center;
  gap: 0.85rem 1.1rem;
  width: 100%;
  min-height: 0;
  /* Inside the scroller, so the chosen card's ring is not clipped at the top. */
  padding: var(--spacing-md) 4px calc(var(--safe-bottom-offset) + 7.5rem);
  overflow-y: auto;
  scrollbar-width: none;

  &.has-review {
    padding-bottom: calc(var(--safe-bottom-offset) + 10rem);
  }

  &::-webkit-scrollbar {
    display: none;
  }

  button {
    padding: 0;
    border: 0;
    background: transparent;
    color: inherit;
    font: inherit;
    cursor: pointer;

    &:disabled {
      cursor: default;
    }
  }
}

.pair-bond.is-chosen .pair-square {
  box-shadow: 0 0 0 2px var(--color-white);
}

.ring {
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  margin-top: var(--spacing-xl);
}

/* Positions and people: the road first, then the percent circle and its legend. */
.collection-content:has(> .road + .ring) {
  display: flex;
  flex-direction: column;

  > .ring {
    display: contents;
  }

  > .road {
    order: 1;
  }

  .donut-anchor {
    order: 2;
    margin-top: var(--spacing-xl);
  }

  .legend-block {
    order: 3;
  }
}

.legend-block {
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-top: var(--spacing-md);

  .legend-title {
    margin-bottom: 0.5rem;
  }
}

.not-counted {
  margin: var(--spacing-l) 0 0;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-white-30);

  .legend-count {
    color: var(--color-white-70);
  }
}

.stage-board {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: var(--spacing-l);
  padding-bottom: calc(var(--safe-bottom-offset) + 3.5rem);
  container-type: size;

  &.is-overview {
    /* Stay as tall as the grid. A flex shrink here clips the board and iPad then has nothing to scroll. */
    flex: none;
    min-height: min-content;
    margin-top: var(--spacing-xl);
    padding: 0;
    container-type: inline-size;

    .board-plot {
      width: 100%;
    }
  }
}

.board-plot {
  --rail: 2.5rem;
  display: grid;
  grid-template-columns: var(--rail) minmax(0, 1fr) var(--rail);
  grid-template-rows: auto auto auto auto;
  column-gap: 0.4rem;
  row-gap: 0.4rem;
  width: min(100%, 100cqh);
}

.board-legend {
  grid-column: 2;
  grid-row: 4;
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.35rem 1rem;
  margin-top: 0.35rem;
}

.board-legend-item {
  display: flex;
  align-items: center;
  gap: 0.35rem;
  color: var(--color-white-50);
  font-size: 11px;
  font-weight: 600;
  line-height: 1;
}

.board-swatch {
  flex: none;
  width: 10px;
  height: 10px;
  box-sizing: border-box;

  &.is-pairs {
    background: var(--color-pink);
  }

  &.is-axes {
    width: 22px;
    background: linear-gradient(to right, #000, var(--color-white));
  }
}

.board-frame {
  grid-column: 2;
  grid-row: 2;
  position: relative;
  width: 100%;
  min-width: 0;
}

.board-grid {
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  grid-template-rows: repeat(8, 1fr);
  aspect-ratio: 1;
  width: 100%;
}

.board-square {
  position: relative;
  background: color-mix(in srgb, var(--color-pink) calc(var(--fill, 0) * 100%), #000);

  &.is-axis {
    background: color-mix(in srgb, var(--color-white) calc(var(--fill, 0) * 100%), #000);
  }
  border-right: 1px solid var(--color-white-20);
  border-bottom: 1px solid var(--color-white-20);

  &.is-first-file {
    border-left: 1px solid var(--color-white-20);
  }

  &.is-top-rank {
    border-top: 1px solid var(--color-white-20);
  }
}

.board-coord {
  position: absolute;
  font-size: 8px;
  font-weight: 700;
  line-height: 1;
  color: var(--color-white-40);
  pointer-events: none;

  .is-current.is-unique > &,
  .is-current.is-unknown > & {
    color: var(--color-white);
  }

  .is-current.is-repeat > & {
    color: var(--color-dark-900);
  }

  &.is-here {
    position: static;
    font-size: 12px;
    font-weight: 700;
  }

  &.is-file {
    bottom: 3px;
    left: 50%;
    transform: translateX(-50%);
  }

  &.is-rank {
    left: 4px;
    top: 50%;
    transform: translateY(-50%);
  }
}

.board-crown {
  pointer-events: none;
  color: var(--color-white-30);

  .is-master & {
    color: var(--color-pink);
  }
}

.file-axis {
  grid-column: 2;
  grid-row: 3;
  display: grid;
  grid-template-columns: repeat(8, 1fr);
  align-items: start;
}

.rank-axis {
  grid-column: 1;
  grid-row: 2;
  display: grid;
  grid-template-rows: repeat(8, 1fr);
  min-height: 0;
}

.axis-slot {
  position: relative;
  min-width: 0;
  min-height: 0;

  .file-axis & {
    justify-self: center;
  }

  .rank-axis & {
    display: flex;
    align-items: center;
    justify-content: flex-end;
  }
}

.axis-dot {
  position: absolute;
  width: 5px;
  height: 5px;
  border-radius: 50%;
  background: color-mix(in srgb, var(--color-white) calc(var(--fill, 0) * 100%), transparent);
  pointer-events: none;

  .file-axis & {
    top: 0;
    left: 50%;
    transform: translate(-50%, -100%);
  }

  .rank-axis & {
    top: 50%;
    right: 0;
    transform: translate(100%, -50%);
  }
}

.axis-count {
  justify-self: center;
  align-self: start;
  padding-top: 2px;
  color: var(--color-white-30);
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  line-height: 1;

  &.is-dot {
    color: color-mix(in srgb, var(--color-white) calc(var(--fill, 0) * 100%), var(--color-white-30));
  }

  &.is-unique {
    color: var(--color-pink);
  }

  &.is-repeat {
    color: var(--color-white);
  }

  &.is-unknown {
    color: var(--color-unknown);
  }

  /* Same line against the board as before. Only the digits turn around. */
  &.is-rank {
    justify-self: end;
    align-self: center;
    padding: 0 3px 0 0;
    writing-mode: vertical-rl;
  }
}

.rank-num {
  display: inline-block;
  transform: rotate(180deg);
}

.axis-name {
  color: var(--color-white-30);
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0;
  line-height: 1;
  text-transform: lowercase;

  &.is-positions {
    grid-column: 2;
    grid-row: 1;
    justify-self: end;
  }

  &.is-people {
    grid-column: 3;
    grid-row: 2;
    align-self: start;
    justify-self: start;
    writing-mode: vertical-rl;
    transform: rotate(180deg);
  }
}

.milestone {
  position: relative;
  z-index: 1;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 2.65rem;
  height: 2.65rem;
  border-radius: 4px;
  background: var(--color-dark-700);
  font-size: 11px;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0;
  line-height: 1;

  &.is-ahead {
    border: 1px solid var(--color-white-20);
    color: var(--color-white-30);
  }

  &.is-unique {
    background: var(--color-pink);
    color: var(--color-white);
  }

  &.is-repeat {
    background: var(--color-white);
    color: var(--color-dark-900);
  }

  &.is-unknown {
    background: var(--color-unknown);
    color: var(--color-white);
  }
}

.donut {
  position: relative;
  flex: none;
  width: 132px;
  height: 132px;
}

.donut.is-hero {
  width: 200px;
  height: 200px;

  .collection-progress {
    font-size: 24px;
    font-weight: 700;
    color: var(--color-white);
  }
}

.donut svg {
  display: block;
  width: 100%;
  height: 100%;
}

.donut-track,
.donut-arc {
  fill: none;
  stroke-width: 10;
}

.donut-track {
  stroke: var(--color-not-played);
}

.donut-arc.is-unique {
  stroke: var(--color-pink);
}

.donut-arc.is-reported {
  stroke: var(--color-reported);
}

.donut-arc.is-repeat {
  stroke: var(--color-white);
}

.donut-arc.is-unknown,
.donut-arc.is-none {
  stroke: var(--color-unknown);
}

.legend-column {
  min-width: 0;
}

/* The view switch sits above the scopes, the way Photos keeps its view control above the tab bar. */
.collection-dock {
  position: absolute;
  z-index: 3;
  right: var(--spacing-l);
  bottom: var(--safe-bottom-offset);
  left: var(--spacing-l);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  pointer-events: none;
}

.collection-review {
  display: flex;
  align-items: center;
  height: 2rem;
  padding: 0 0.85rem;
  border: 1px solid var(--color-white-10);
  border-radius: 2rem;
  background: var(--color-dark-5);
  backdrop-filter: blur(8px);
  color: var(--color-white-70);
  font-family: inherit;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  white-space: nowrap;
  pointer-events: auto;
  cursor: pointer;

  @include press;
}

.collection-lens {
  --nav-cell-size: 2rem;
  --nav-pill-gap: 0.125rem;
  pointer-events: auto;

  :deep(.NavPillButton) {
    color: var(--color-white-30);
  }

  :deep(.NavPillButton:hover:not(.is-chosen)) {
    color: var(--color-white-60);
  }

  :deep(.NavPillButton:active) {
    transform: none;
  }

  :deep(.NavPillButton.is-chosen) {
    color: var(--color-white);
  }
}

.collection-dock-row {
  display: grid;
  grid-template-columns: 3.5rem 1fr 3.5rem;
  align-items: start;
  width: 100%;
  pointer-events: none;
}

.collection-scopes {
  position: relative;
  grid-column: 2;
  justify-self: center;
  display: flex;
  align-items: flex-start;
  gap: 0.15rem;
  padding: 0 0.35rem;
  pointer-events: auto;

  /* Stay in the center column, under the lens. A wider min-width spills into the close button and the row stops being centered. */
  &.is-wide {
    gap: 0;
    padding: 0 0.1rem;

    .collection-scope {
      min-width: 2.5rem;
    }
  }

  /* The capsule covers the icons. The words sit below it. */
  &::before {
    content: '';
    position: absolute;
    inset: 0 0 auto 0;
    height: 2.75rem;
    border: 1px solid var(--color-white-10);
    border-radius: 2.75rem;
    background: var(--color-dark-5);
    backdrop-filter: blur(8px);
  }
}

.collection-scope {
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  min-width: 4.25rem;
  padding: 0;
  border: 0;
  background: none;
  color: var(--color-white-30);
  font-family: inherit;
  cursor: pointer;

  &.is-chosen {
    color: var(--color-white);
  }

  @include hover-capable {
    &:hover:not(.is-chosen) {
      color: var(--color-white-50);
    }
  }

  @include press;
}

.collection-scope-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 2.75rem;
}

.collection-scope-hash {
  font-size: 16px;
  font-weight: 700;
  line-height: 1;
}

.collection-scope-board {
  display: block;
  width: 13px;
  height: 13px;
  box-sizing: border-box;
  border: 1px solid currentColor;
  border-radius: 2px;
  background-repeat: no-repeat;
  background-size: 50% 50%;
  background-position:
    0 0,
    100% 100%;
  background-image: linear-gradient(currentColor, currentColor), linear-gradient(currentColor, currentColor);
}

.collection-scope-label {
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.02em;
  line-height: 1;
  white-space: nowrap;
}

.collection-progress {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0;
  font-size: 13px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;
  color: var(--color-white-40);
}

/* Says what the three marks are about, quietly: not a heading of its own. */
.legend-title {
  margin-bottom: 0.4rem;
  font-size: 10px;
  font-weight: 600;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: var(--color-white-30);
}

.legend {
  display: flex;
  flex-direction: column;
  gap: 0.45rem;
}

.legend.is-row {
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: center;
  gap: 0.75rem;
}

.legend-item {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.2rem;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  color: var(--color-white-50);
}

.legend-line {
  display: flex;
  align-items: center;
  gap: 0.35rem;
}

.legend-pct {
  font-variant-numeric: tabular-nums;
  letter-spacing: 0.04em;

  &.is-unique {
    color: var(--color-pink);
  }

  &.is-reported {
    color: var(--color-reported);
  }

  &.is-repeat {
    color: var(--color-white);
  }

  &.is-unknown,
  &.is-none {
    color: var(--color-unknown);
  }
}

.legend-count {
  font-variant-numeric: tabular-nums;
  color: var(--color-white);
}

.swatch {
  width: 8px;
  height: 8px;
  border-radius: 2px;
}

.swatch.is-unique {
  background: var(--color-pink);
}

.swatch.is-reported {
  background: var(--color-reported);
}

.swatch.is-repeat {
  background: var(--color-white);
}

.swatch.is-unknown,
.swatch.is-none {
  background: var(--color-unknown);
}

.position-detail {
  position: sticky;
  top: 0;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 0.35rem;
  padding-top: var(--spacing-l);
  padding-bottom: var(--spacing-l);
  background: var(--color-dark-700);

  :deep(.rank-line) {
    align-items: stretch;
  }

  /* The photo row collapses its gap so the picture tucks under the rank. Play still needs air. */
  :deep(.rank-side) {
    margin-left: 0.75rem;
  }
}

.position-play {
  display: flex;
  align-items: center;
  justify-content: center;
  align-self: stretch;
  padding: 0 0.85rem;
  border: 1px solid var(--color-pink-30);
  border-radius: var(--radius-x-small);
  background: transparent;
  color: var(--color-white);
  font-family: inherit;
  font-size: 11px;
  font-weight: 700;
  letter-spacing: 0.06em;
  line-height: 1;
  text-transform: uppercase;
  white-space: nowrap;
  cursor: pointer;

  &:active {
    transform: scale(0.96);
  }
}

.position-tone-line {
  margin: 0;
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.04em;
  line-height: 1;

  &.is-unique {
    color: var(--color-pink);
  }

  &.is-reported {
    color: var(--color-reported);
  }

  &.is-repeat {
    color: var(--color-white);
  }

  &.is-unknown {
    color: var(--color-unknown);
  }
}

.position-table {
  display: grid;
  grid-template-columns: repeat(10, minmax(0, 1fr));
  gap: 2px;
  width: 100%;
}

.position-cell {
  aspect-ratio: 1;
  padding: 0;
  border: 1px solid var(--color-white-10);
  border-radius: 2px;
  background: transparent;
  color: var(--color-white-30);
  font-family: inherit;
  font-size: 8px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  cursor: pointer;

  &.is-unique {
    border-color: transparent;
    background: var(--color-pink);
    color: var(--color-white);
  }

  &.is-reported {
    border-color: transparent;
    background: var(--color-reported);
    color: var(--color-white);
  }

  &.is-repeat {
    border-color: transparent;
    background: var(--color-white);
    color: var(--color-dark-900);
  }

  &.is-unknown {
    border-color: transparent;
    background: var(--color-unknown);
    color: var(--color-white);
  }

  &.is-empty {
    border-color: transparent;
    background: var(--color-not-played);
    color: var(--color-white-30);
  }

  &.is-selected {
    outline: 1px solid var(--color-white);
    outline-offset: 1px;
  }
}

.empty-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: var(--spacing-md);
  padding: var(--spacing-xxl) 0;
  color: var(--color-white-20);
  font-size: 14px;
}

.collection-close {
  grid-column: 3;
  justify-self: end;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3.5rem;
  height: 3.5rem;
  margin-top: -0.375rem;
  padding: 0;
  border: 1px solid var(--color-white-10);
  border-radius: 3.5rem;
  background-color: var(--color-dark-5);
  backdrop-filter: blur(8px);
  color: var(--color-white-70);
  font: inherit;
  cursor: pointer;
  pointer-events: auto;
  transition: all 200ms linear;

  @include hover {
    background-color: var(--color-dark-20);
    border-color: var(--color-white-30);
    color: var(--color-white);
  }

  @include press;
}

.collection-overlay-enter-active,
.collection-overlay-leave-active {
  transition: opacity 200ms ease;

  .collection-wrapper {
    transition: transform 200ms ease;
  }
}

.collection-overlay-enter-from,
.collection-overlay-leave-to {
  opacity: 0;

  .collection-wrapper {
    transform: translateY(20px);
  }
}

.collection-overlay-enter-from .is-mobile,
.collection-overlay-leave-to .is-mobile {
  transform: translateY(100%);
}

/* The circle grows on the card alone. The wrapper still carries the bloom, but a
   radius on that same box clips the face that stands above the drawer.
   Only the backdrop fades: a translucent drawer lets the page show through the
   part of the face above the drawer edge. */
.collection-overlay-enter-active.from-sphere,
.collection-overlay-leave-active.from-sphere {
  transition: none;

  &::before {
    transition: opacity 360ms ease;
  }

  .collection-wrapper {
    transition: transform 360ms ease;
  }

  .collection-card {
    transition: border-radius 360ms ease;
  }

  .collection-brand {
    transition: opacity 360ms ease;
  }
}

/* The radius repaints on the main thread while the scale is composited, so the
   corners settle early instead of trailing the growing drawer. */
.collection-overlay-enter-active.from-sphere .collection-card {
  transition: border-radius 240ms cubic-bezier(0.2, 0.8, 0.2, 1);
}

/* The drawer stays solid while it shrinks, so a slow finish would show it
   crawling into the sphere. Closing accelerates instead. */
.collection-overlay-leave-active.from-sphere {
  .collection-wrapper,
  .collection-card,
  .collection-brand {
    transition-timing-function: cubic-bezier(0.4, 0, 1, 1);
  }
}

.collection-overlay-enter-from.from-sphere,
.collection-overlay-leave-to.from-sphere {
  opacity: 1;

  &::before {
    opacity: 0;
  }

  .collection-wrapper {
    transform: translate(var(--bloom-x), var(--bloom-y)) scale(var(--bloom-sx), var(--bloom-sy));
  }

  .collection-card {
    border-radius: 50%;
  }

  .collection-brand {
    opacity: 0;
  }
}
</style>
